import React from "react";
import { Link, NavLink, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowRight,
  Award,
  BadgeCheck,
  Building2,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  CircleHelp,
  Clock3,
  Copy,
  Banknote,
  ClipboardCheck,
  Eye,
  FileCheck,
  FilePenLine,
  FileText,
  Globe2,
  Earth,
  Headphones,
  Languages,
  ListChecks,
  Mail,
  MapPin,
  Menu,
  Phone,
  Radar,
  Scale,
  Shield,
  ShieldCheck,
  DollarSign,
  TriangleAlert,
  TrendingUp,
  Users,
  UserX,
  X,
  Zap } from
"lucide-react";
import content from "./data/siteContent.js";
import teamFounderImage from "./assets/team/team-founder.jpg";
import teamChairmanImage from "./assets/team/team-chairman.jpg";
import teamLawyer1Image from "./assets/team/team-lawyer1.jpg";
import teamLawyer2Image from "./assets/team/team-lawyer2.jpg";
import teamLawyer3Image from "./assets/team/team-lawyer3.jpg";
import teamDirectorImage from "./assets/team/team-director.jpg";

const slugOf = (value = "") => encodeURIComponent(String(value).trim());
const matchesSlug = (candidate, slug = "") => candidate === slug || slugOf(candidate) === slug;
const cx = (...values) => values.filter(Boolean).join(" ");
const ADMIN_TOKEN_KEY = "cic-admin-token";
const ADMIN_LEADS_PAGE_SIZE = 20;
const CUSTOMER_SERVICE_SCRIPT_URL = "http://localhost:8000/index/index/chatBoxJs/u/679c2041184f2";
const CUSTOMER_SERVICE_KEFU_CODE = "69d4b84eced45";
const CUSTOMER_SERVICE_AVATAR = "/static/common/images/customer/visitor-circle-1.png";
const uiText = (key) => key.split(".").reduce((value, part) => value?.[part], content.uiText) ?? key;
const defaultContent = JSON.parse(JSON.stringify(content));

const isObject = (value) => value && typeof value === "object" && !Array.isArray(value);

const deepMerge = (base, patch) => {
  if (!isObject(base) || !isObject(patch)) return patch;
  const merged = { ...base };
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) continue;
    merged[key] = isObject(value) && isObject(base[key]) ? deepMerge(base[key], value) : value;
  }
  return merged;
};

const mergeRuntimeContent = (patch) => {
  if (!isObject(patch)) return null;
  return deepMerge(defaultContent, patch);
};

const applyRuntimeContent = (patch) => {
  const nextContent = mergeRuntimeContent(patch);
  if (!nextContent) return null;
  Object.assign(content, nextContent);
  return nextContent;
};

const jsonFetch = async (url, options = {}) => {
  const headers = new Headers(options.headers || {});
  if (options.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  const response = await fetch(url, { ...options, headers });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || `Request failed: ${response.status}`);
  }
  return data;
};

const trackTikTok = (eventName, params) => {
  if (typeof window === "undefined" || !window.ttq || typeof window.ttq.track !== "function") return;
  window.ttq.track(eventName, params);
};

const getCustomerServiceSettings = () => ({
  scriptUrl: content.contact?.customerServiceScriptUrl?.trim() || "",
  kefuCode: content.contact?.customerServiceKefuCode?.trim() || "",
  avatar: content.contact?.customerServiceAvatar?.trim() || CUSTOMER_SERVICE_AVATAR
});

const hasCustomerService = () => {
  const { scriptUrl, kefuCode } = getCustomerServiceSettings();
  return Boolean(scriptUrl && kefuCode);
};

const goToContactForm = () => {
  if (typeof window === "undefined") return;
  if (window.location.pathname === "/contact") {
    document.getElementById("form")?.scrollIntoView({ behavior: "smooth", block: "start" });
    return;
  }
  window.location.assign("/contact?focus=form");
};

const loadCustomerServiceScript = (settings = getCustomerServiceSettings()) => {
  if (typeof window === "undefined") return Promise.resolve(false);
  if (!settings.scriptUrl) return Promise.resolve(false);
  if (window.whisperChat) return Promise.resolve(true);
  const expectedSrc = new URL(settings.scriptUrl, document.baseURI).href;
  const existing = Array.from(document.querySelectorAll('script[data-customer-service-loader="1"]'))
    .find((script) => script.src === expectedSrc);
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve(true), { once: true });
      existing.addEventListener("error", reject, { once: true });
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = settings.scriptUrl;
    script.async = true;
    script.dataset.customerServiceLoader = "1";
    script.addEventListener("load", () => resolve(true), { once: true });
    script.addEventListener("error", reject, { once: true });
    document.head.appendChild(script);
  });
};

const initCustomerService = (settings = getCustomerServiceSettings()) => {
  if (typeof window === "undefined" || !window.whisperChat?.init) return false;
  if (!settings.kefuCode) return false;
  const settingsKey = `${settings.scriptUrl}|${settings.kefuCode}|${settings.avatar}`;
  if (window.__cicCustomerServiceKey !== settingsKey) {
    window.whisperChat.init({
      avatar: settings.avatar,
      kefuCode: settings.kefuCode
    });
    window.__cicCustomerServiceKey = settingsKey;
  }
  return true;
};

const showCustomerService = () => {
  const launcher = document.getElementById("WS-SHOW-CHAT");
  if (launcher) {
    launcher.click();
    return true;
  }
  window.postMessage("show_chat", "*");
  return false;
};

const openCustomerService = () => {
  trackTikTok("ClickButton");
  const settings = getCustomerServiceSettings();
  if (!settings.scriptUrl || !settings.kefuCode) {
    goToContactForm();
    return;
  }
  if (initCustomerService(settings)) {
    if (!showCustomerService()) setTimeout(showCustomerService, 0);
    return;
  }
  loadCustomerServiceScript(settings)
    .then(() => {
      initCustomerService(settings);
      setTimeout(showCustomerService, 0);
    })
    .catch(goToContactForm);
};
const teamImageByIndex = [
teamFounderImage,
teamChairmanImage,
teamLawyer1Image,
teamLawyer2Image,
teamLawyer3Image,
teamDirectorImage,
teamLawyer1Image];


const memberKey = (member = {}, fallback = "") => {
  const slug = member.slug?.trim();
  if (slug) return slug;
  return String(member.name || fallback).trim().toLowerCase().replace(/\s+/g, "-");
};

const memberGroup = (member = {}) => member.teamGroup === "investigator" ? "investigator" : "lawyer";

const memberPracticeYears = (member = {}) => {
  const yearUnit = uiText("team.practice_year_unit");
  const explicit = member.practiceYears?.trim();
  if (explicit) return `${explicit.replace(new RegExp(`${yearUnit}$`), "")}${yearUnit}`;
  const matched = member.exp?.match(new RegExp(`(\\d+)\\s*${yearUnit}`));
  return matched ? `${matched[1]}${yearUnit}` : member.exp;
};

function useCountUp(target, duration = 2000, visible = false) {
  const [value, setValue] = React.useState(0);
  const startedRef = React.useRef(false);

  React.useEffect(() => {
    if (!visible || startedRef.current) return;
    startedRef.current = true;
    const start = performance.now();
    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.round(eased * target));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [duration, target, visible]);

  return value;
}

function useSeo(title, description) {
  React.useEffect(() => {
    document.title = title || content.seo.defaultTitle;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", description || content.seo.defaultDescription);
  }, [title, description]);
}

function useRuntimeContent() {
  const [revision, setRevision] = React.useState(0);

  React.useEffect(() => {
    let cancelled = false;
    fetch("/api/content", { headers: { Accept: "application/json" } }).
    then((response) => response.ok ? response.json() : null).
    then((data) => {
      if (!cancelled && applyRuntimeContent(data)) {
        setRevision((value) => value + 1);
      }
    }).
    catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  return revision;
}

function useTikTokPixel(revision) {
  const location = useLocation();
  const pixelId = content.seo?.tiktokPixelId?.trim();

  React.useEffect(() => {
    if (!pixelId || typeof window === "undefined") return;
    if (!window.ttq) {
      const ttq = window.ttq = [];
      window.TiktokAnalyticsObject = "ttq";
      ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie", "holdConsent", "revokeConsent", "grantConsent"];
      ttq.setAndDefer = (target, method) => {
        target[method] = (...args) => target.push([method].concat(Array.prototype.slice.call(args, 0)));
      };
      ttq.methods.forEach((method) => ttq.setAndDefer(ttq, method));
      ttq.instance = (id) => {
        ttq._i = ttq._i || {};
        const instance = ttq._i[id] = ttq._i[id] || [];
        ttq.methods.forEach((method) => ttq.setAndDefer(instance, method));
        return instance;
      };
      ttq.load = (id, options = {}) => {
        ttq._i = ttq._i || {};
        ttq._i[id] = ttq._i[id] || [];
        const scriptUrl = "https://analytics.tiktok.com/i18n/pixel/events.js";
        ttq._i[id]._u = scriptUrl;
        ttq._t = ttq._t || {};
        ttq._t[id] = Date.now();
        ttq._o = ttq._o || {};
        ttq._o[id] = options;
        if (document.querySelector(`script[data-tiktok-pixel-id="${id}"]`)) return;
        const script = document.createElement("script");
        script.type = "text/javascript";
        script.async = true;
        script.dataset.tiktokPixelId = id;
        script.src = `${scriptUrl}?sdkid=${id}&lib=ttq`;
        const firstScript = document.getElementsByTagName("script")[0];
        firstScript?.parentNode?.insertBefore(script, firstScript) || document.head.appendChild(script);
      };
    }
    if (window.__cicTikTokPixelId !== pixelId && typeof window.ttq.load === "function") {
      window.ttq.load(pixelId);
      window.__cicTikTokPixelId = pixelId;
    }
  }, [pixelId, revision]);

  React.useEffect(() => {
    if (!pixelId || !window.ttq || typeof window.ttq.page !== "function") return;
    window.ttq.page();
  }, [pixelId, location.pathname, location.search]);
}

function useCustomerService(revision) {
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    if (revision === 0 || !hasCustomerService()) return;
    const settings = getCustomerServiceSettings();
    loadCustomerServiceScript(settings).then(() => initCustomerService(settings)).catch(() => {});
  }, [revision]);
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();

  React.useEffect(() => {
    if (hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, hash]);

  return null;
}

function App() {
  const revision = useRuntimeContent();
  useTikTokPixel(revision);
  useCustomerService(revision);

  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/advantages" element={<AdvantagesPage mode="advantages" />} />
        <Route path="/services" element={<AdvantagesPage mode="services" />} />
        <Route path="/process" element={<ProcessPage mode="process" />} />
        <Route path="/how-it-works" element={<ProcessPage mode="how-it-works" />} />
        <Route path="/testimonials" element={<TestimonialsPage />} />
        <Route path="/cases" element={<TestimonialsPage />} />
        <Route path="/team" element={<TeamPage />} />
        <Route path="/scam-types" element={<ScamTypesPage />} />
        <Route path="/scam-types/:slug" element={<ScamTypeDetail />} />
        <Route path="/global-network" element={<NetworkPage />} />
        <Route path="/regions/:slug" element={<RegionDetail />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/assessment" element={<ContactPage assessment />} />
        <Route path="/free-consultation" element={<ContactPage assessment />} />
        <Route path="/privacy" element={<LegalPage kind="privacy" />} />
        <Route path="/disclaimer" element={<LegalPage kind="disclaimer" />} />
        <Route path="/news/:id" element={<NewsPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>);

}

function Layout({ children, dark = false }) {
  const location = useLocation();
  const hideChrome = location.pathname.startsWith("/admin");

  if (hideChrome) return children;

  return (
    <div className={dark ? "site site-dark" : "site"}>
      <Header />
      <main>{children}</main>
      <Footer />
      <FloatingConsult />
    </div>);

}

function Header() {
  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const [activeHash, setActiveHash] = React.useState("#hero");
  const nav = content.navLinks ?? [];
  const location = useLocation();
  const navigate = useNavigate();
  const sectionLinks = React.useMemo(() => nav.filter((item) => item.href.startsWith("#") || item.href.startsWith("/#")), [nav]);
  const activeNavClass = (href = "/") => {
    if (href === "#contact" || href === "/#contact") return location.pathname === "/contact" ? "active" : "";
    if (href === "/" || href === "#hero") {
      return location.pathname === "/" && (!activeHash || activeHash === "#hero") ? "active" : "";
    }
    if (href.startsWith("/#")) {
      return location.pathname === "/" && activeHash === `#${href.slice(2)}` ? "active" : "";
    }
    if (href.startsWith("#")) return location.pathname === "/" && activeHash === href ? "active" : "";
    if (href.startsWith("/")) return location.pathname === href.split("#")[0] ? "active" : "";
    return "";
  };
  const handleNavClick = (href = "/") => {
    setOpen(false);

    if (href === "#contact" || href === "/#contact") {
      navigate("/contact");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    if (href === "/" || href === "#hero") {
      navigate("/");
      window.requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
      return;
    }

    if (href.startsWith("/#")) {
      navigate({ pathname: "/", hash: href.slice(2) });
      return;
    }

    if (href.startsWith("/")) {
      navigate(href);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    if (href.startsWith("#")) {
      if (location.pathname !== "/") {
        navigate({ pathname: "/", hash: href.slice(1) });
        return;
      }

      document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  React.useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener("resize", close);
    return () => window.removeEventListener("resize", close);
  }, [open]);

  React.useEffect(() => {
    const update = () => {
      setScrolled(window.scrollY > 40);
      if (location.pathname !== "/") return;

      const ids = sectionLinks.
      map((item) => {
        if (item.href.startsWith("/#")) return item.href.slice(2);
        if (item.href.startsWith("#")) return item.href.slice(1);
        return "";
      }).
      filter(Boolean);

      const current = ids.
      map((id) => ({ id, top: document.getElementById(id)?.getBoundingClientRect().top })).
      filter((item) => typeof item.top === "number" && item.top <= 120).
      sort((a, b) => b.top - a.top)[0];

      if (current) setActiveHash(`#${current.id}`);
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    return () => window.removeEventListener("scroll", update);
  }, [location.pathname, sectionLinks]);

  React.useEffect(() => {
    if (location.pathname === "/" && location.hash) setActiveHash(location.hash);
  }, [location.pathname, location.hash]);

  React.useEffect(() => {
    if (location.pathname !== "/" || !location.hash) return;
    window.requestAnimationFrame(() => {
      document.querySelector(location.hash)?.scrollIntoView({ behavior: "smooth" });
    });
  }, [location.pathname, location.hash]);

  return (
    <header className={`header${scrolled || open ? " is-scrolled" : ""}`}>
      <button type="button" className="brand" aria-label={uiText("layout.cic")} onClick={() => handleNavClick("/")}>
        <img src="/logo.png" alt="" />
        <span>
          <strong>{content.brandName}</strong>
          <small>{uiText("layout.brand_full_name_en")}</small>
        </span>
      </button>
      <nav className="desktop-nav" aria-label={uiText("layout.text_e4b8bbe5af")}>
        {nav.map((item) =>
        <button key={`${item.href}-${item.label}`} type="button" onClick={() => handleNavClick(item.href)} className={activeNavClass(item.href)}>
            {item.label}
          </button>
        )}
      </nav>
      <button className="icon-button mobile-toggle" type="button" onClick={() => setOpen((v) => !v)} aria-label={uiText("layout.text_e68993e5bc")}>
        {open ? <X /> : <Menu />}
      </button>
      {open &&
      <nav className="mobile-nav" aria-label={uiText("layout.text_e7a7bbe58a")}>
          {nav.map((item) =>
        <button key={`${item.href}-${item.label}`} type="button" className={activeNavClass(item.href)} onClick={() => handleNavClick(item.href)}>
              {item.label}
            </button>
        )}
        </nav>
      }
    </header>);

}

function Footer() {
  const footer = content.footer;
  const services = (content.scamTypes.items ?? []).slice(0, 6);
  const logoUrl = content.seo?.logoUrl?.trim() || "/logo.png?v=3";
  return (
    <footer className="bg-[#111827] text-white pt-8 sm:pt-12 pb-8 sm:pb-10 section-padding border-t border-white/10">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-10 lg:gap-8">
          <div className="space-y-2.5 sm:space-y-3">
            <div className="flex items-center gap-3">
              {logoUrl ?
              <img src={logoUrl} alt="" className="h-11 w-auto max-h-12 max-w-[11rem] sm:max-w-[13rem] object-contain object-left" loading="lazy" /> :

              <Globe2 className="h-10 w-10 text-amber-400 shrink-0" aria-hidden="true" />
              }
              <span className="font-semibold text-[15px] sm:text-base tracking-tight">{content.brandName}</span>
            </div>
            {footer.blurb ? <p className="text-[12.5px] sm:text-sm text-gray-400 leading-relaxed">{footer.blurb}</p> : null}
            <p className="text-xs text-gray-500 pt-1">{footer.copyright}</p>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5 pt-2 text-xs text-gray-500">
              <FooterLink href="/privacy">{uiText("layout.text_e99a90e7a7")}</FooterLink>
              <FooterLink href="/disclaimer">{uiText("layout.text_e6b395e5be")}</FooterLink>
              {(footer.links ?? []).map((label) => <span className="text-gray-500" key={label}>{label}</span>)}
            </div>
            <p className="text-[11px] text-gray-600 leading-relaxed pt-3 max-w-md">{uiText("layout.text_e69cace7ab")}
              {" "}
              <FooterLink href="/disclaimer">{uiText("layout.text_e5858de8b4")}</FooterLink>。
            </p>
          </div>

          <div>
            <h3 className="text-[13px] sm:text-sm font-semibold text-white mb-2.5 sm:mb-3.5 tracking-wide">{footer.quickLinksHeading ?? uiText("layout.text_e5bfabe980")}</h3>
            <div className="grid grid-cols-2 gap-x-8 gap-y-0">
              <div className="space-y-0">
                {(footer.quickLinksLeft ?? []).map((link) => <FooterLink href={link.href} key={`${link.href}-${link.label}`}>{link.label}</FooterLink>)}
              </div>
              <div className="space-y-0">
                {(footer.quickLinksRight ?? []).map((link) => <FooterLink href={link.href} key={`${link.href}-${link.label}`}>{link.label}</FooterLink>)}
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-[13px] sm:text-sm font-semibold text-white mb-2.5 sm:mb-3.5 tracking-wide">{footer.serviceHeading ?? uiText("layout.text_e69c8de58a")}</h3>
            <div className="space-y-0">
              {services.map((item) => <FooterLink href={`/scam-types/${slugOf(item.title)}`} key={item.title}>{item.title}</FooterLink>)}
            </div>
          </div>

          <div>
            <h3 className="text-[13px] sm:text-sm font-semibold text-white mb-2.5 sm:mb-3.5 tracking-wide">{footer.networkHeading ?? uiText("layout.text_e585a8e790")}</h3>
            <div className="space-y-0">
              {(footer.networkLinks ?? []).map((link) => <FooterLink href={link.href} key={`${link.href}-${link.label}`}>{link.label}</FooterLink>)}
            </div>
          </div>
        </div>

        <section aria-label={uiText("layout.text_e4bfa1e4bb")} className="mt-10 border-t border-white/10 pt-8 sm:mt-12 sm:pt-10">
          <ul className="mx-auto max-w-4xl space-y-2 text-center text-[12px] leading-relaxed text-gray-400 sm:text-[13px]">
            <li>{footer.copyright}</li>
            <li>
              <FooterLink href="/privacy">{uiText("layout.text_e99a90e7a7")}</FooterLink>
              <span className="px-1.5 text-gray-600" aria-hidden="true">|</span>
              <FooterLink href="/disclaimer">{uiText("layout.text_e5858de8b4")}</FooterLink>
              <span className="px-1.5 text-gray-600" aria-hidden="true">|</span>
              <FooterLink href="/contact">{uiText("layout.text_e88194e7b3")}</FooterLink>
            </li>
            <li>{uiText("layout.36_14")}</li>
            <li>{uiText("layout.24")}</li>
            <li className="text-[11px] text-gray-500 sm:text-xs">{uiText("layout.text_e585b7e4bd")}</li>
          </ul>
        </section>
      </div>
    </footer>);

}

function FooterLink({ href, children }) {
  const className = "text-[13px] sm:text-sm text-gray-400 hover:text-white transition-colors block py-1";
  const scrollTop = () => window.requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: "auto" }));
  if (href?.startsWith("/")) return <Link to={href} className={className} onClick={scrollTop}>{children}</Link>;
  return <a href={href} className={className}>{children}</a>;
}

function FloatingConsult() {
  const navigate = useNavigate();
  const location = useLocation();
  if (location.pathname.startsWith("/contact")) return null;
  const handleClick = () => {
    trackTikTok("Contact", { description: `${location.pathname}${location.search || ""}` });
    navigate("/contact?focus=form");
  };
  return (
    <button
      type="button"
      id="anli-floating-consult-cta"
      data-anli-floating-consult-cta="1"
      className="fixed right-3 max-[380px]:right-2 z-[2147483646] bottom-[calc(5.75rem+env(safe-area-inset-bottom,0px))] click-bounce inline-flex min-h-[42px] items-center justify-center rounded-md px-5 py-2.5 text-[13px] font-semibold tracking-wide shadow-lg shadow-black/20 anli-btn-dark border border-black/10"
      aria-label={uiText("layout.text_e7ab8be58d")}
      onClick={handleClick}>
      
      {content.contact.floatingConsultCta || uiText("layout.text_e7ab8be58d_2")}
    </button>);

}

function HomePage() {
  useSeo(content.seo.defaultTitle, content.seo.defaultDescription);
  return (
    <Layout dark>
      <Hero />
      <PromoVideo />
      <AboutSection preview />
      <NetworkSection preview />
      <TeamSection preview />
      <AdvantagesSection preview />
      <ProcessSection preview />
      <TestimonialsSection preview />
      <RiskSection />
      <ScamTypesSection preview />
      <FAQSection />
      <CTA title={content.contact.title} subtitle={content.contact.subtitle} cta={content.contact.buttonText} href="/contact" />
    </Layout>);

}

function Hero() {
  const hero = content.hero;
  const scrollToProcess = () => {
    window.history.pushState(null, "", "/#process");
    document.querySelector("#process")?.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <section className="hero" id="hero">
      <picture>
        <source srcSet="/images/hero.webp" type="image/webp" />
        <img className="hero-bg" src={hero.coverImage} alt="" />
      </picture>
      <div className="hero-overlay" />
      <div className="hero-inner">
        <div className="animate-fade-in-up">
          <div className="hero-badge">
            <Globe2 size={16} aria-hidden="true" />
            <span>{hero.badge}</span>
          </div>
        </div>
        <h1 className="animate-fade-in-up hero-anim-1">{hero.seoH1 || hero.title}</h1>
        <p className="hero-subtitle animate-fade-in-up hero-anim-2">{hero.subtitle}</p>
        <p className="animate-fade-in-up hero-anim-3">{hero.description}</p>
        {(hero.quote || hero.quoteAuthor) &&
        <div className="quote-panel animate-fade-in-up hero-anim-4">
            {hero.quote && <p>{hero.quote}</p>}
            {hero.quoteAuthor && <strong>{hero.quoteAuthor}</strong>}
          </div>
        }
        <div className="hero-actions animate-fade-in-up hero-anim-5">
          <button className="btn btn-gold" type="button" onClick={openCustomerService}>
            {hero.primaryCta}
          </button>
          <button className="btn btn-ghost" type="button" onClick={scrollToProcess}>
            {hero.secondaryCta}
          </button>
        </div>
        {hero.mediaMentions?.length > 0 &&
        <div className="media-row animate-fade-in-up hero-anim-6">
            <small>{uiText("home.text_e69bbee8a2")}</small>
            <span>
              {hero.mediaMentions.map((name) =>
            <em key={name}>{name}</em>
            )}
            </span>
          </div>
        }
        <div className="trust-row animate-fade-in-up hero-anim-7">
          {hero.trustBadges.map((badge) =>
          <span key={badge}>
              <ShieldCheck size={16} />
              {badge}
            </span>
          )}
        </div>
        {hero.locations?.length > 0 &&
        <div className="location-row animate-fade-in-up hero-anim-8">
            {hero.locations.map((place) =>
          <span key={place}>
                <MapPin size={13} />
                {place}
              </span>
          )}
          </div>
        }
      </div>
    </section>);

}

function PromoVideo() {
  const streamBaseUrl = "https://customer-mta3j679vsu5p01j.cloudflarestream.com/773425e237dcc022b11852f2622e23b1";
  const streamPoster = encodeURIComponent(`${streamBaseUrl}/thumbnails/thumbnail.jpg?time=&height=600`);
  const streamIframeUrl = `${streamBaseUrl}/iframe?autoplay=false&muted=false&loop=true&controls=true&poster=${streamPoster}`;

  return (
    <section id="promo-video" className="promo-video" aria-label={uiText("home.text_e5aea3e4bc")}>
      <div className="promo-video-inner">
        <div className="promo-video-frame">
          <div className="promo-video-aspect">
            <iframe
              src={streamIframeUrl}
              title={uiText("home.text_e5aea3e4bc")}
              loading="lazy"
              allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen;"
              allowFullScreen
            />
          </div>
        </div>
      </div>
    </section>);

}

function PageShell({ eyebrow, title, subtitle, children, className = "" }) {
  const contactHero = className.includes("contact-hero");
  const shellClass = contactHero ? "page-shell-contact-title" : "page-shell-title";
  return (
    <Layout>
      <section className={`${shellClass} border-b ${contactHero ? "border-neutral-200/70 bg-white" : "border-neutral-200/55 bg-white/92 backdrop-blur-[2px]"} ${className}`}>
        <div className={contactHero ? "mx-auto max-w-3xl px-4 py-12 text-center sm:px-6 sm:py-14 lg:py-16" : "mx-auto max-w-7xl px-4 py-7 sm:px-6 sm:py-10 lg:px-8 lg:py-12"}>
          {eyebrow &&
          <p className={contactHero ? "mb-3 text-xs font-semibold uppercase tracking-[0.22em] text-amber-700/95" : "mb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-700/90 sm:mb-3 sm:text-xs"}>
              {eyebrow}
            </p>
          }
          <h1 className={contactHero ? "font-sans text-[2rem] font-bold tracking-tight text-neutral-900 sm:text-4xl lg:text-[2.75rem] lg:leading-tight" : "font-sans text-[1.72rem] font-bold tracking-tight text-neutral-900 sm:text-3xl lg:text-4xl"}>
            {title}
          </h1>
          {subtitle &&
          <p className={contactHero ? "mx-auto mt-4 max-w-xl text-[15px] leading-relaxed text-neutral-600 sm:text-[17px] sm:leading-[1.65]" : "mt-2 max-w-2xl text-[13px] leading-relaxed text-neutral-600 sm:text-[15px]"}>
              {subtitle}
            </p>
          }
        </div>
      </section>
      {children}
    </Layout>);

}

function Section({ id, eyebrow, title, subtitle, children, className = "" }) {
  return (
    <section {...id ? { id } : {}} className={`section ${className}`}>
      {(eyebrow || title || subtitle) &&
      <div className="section-head">
          {eyebrow && <span className="eyebrow dark">{eyebrow}</span>}
          {title && <h2>{title}</h2>}
          {subtitle && <p>{subtitle}</p>}
        </div>
      }
      {children}
    </section>);

}

function ContentAttribution() {
  return (
    <footer aria-label={uiText("home.text_e58685e5ae")} className="border-t border-neutral-200/90 bg-neutral-50/80 px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-7xl">
        <p className="text-center text-[12px] leading-relaxed text-neutral-600 sm:text-[13px]">
          <span>{uiText("home.text_e58685e5ae_2")}</span>
          <span className="mx-1.5 text-neutral-400" aria-hidden="true">·</span>
          <time dateTime="2026-03-28">{uiText("home.2026_3_28")}</time>
        </p>
      </div>
    </footer>);

}
function AboutPage() {
  const about = content.about;
  useSeo(about.browserTitle, about.browserDescription);
  return (
    <PageShell
      eyebrow={uiText("about.cic_2")}
      title={about.seoH1}
      subtitle={content.about.eeatBlocks?.whoParagraphs?.[0] || about.paragraphs[0]}
      className="about-hero-shell">
      
      <div className="about-page-surface">
        <AboutEeatSection />
        <AboutSection />
      </div>
      <ContentAttribution />
    </PageShell>);

}

function AboutEeatSection() {
  const blocks = content.about.eeatBlocks;
  if (!blocks) return null;
  const navItems = [
  { id: "about-who", label: uiText("about.text_e68891e4bb") },
  { id: "about-how", label: uiText("about.text_e5b7a5e4bd") },
  { id: "about-compliance", label: uiText("about.text_e59088e8a7") },
  { id: "about-contact", label: uiText("about.text_e88194e7b3_2") },
  { id: "about-mission", label: uiText("about.text_e4bdbfe591") },
  { id: "about-values", label: uiText("about.text_e4bbb7e580") },
  { id: "about-team-intro", label: uiText("about.text_e59ba2e998") }];

  const items = [
  { id: "about-who", icon: <Scale />, title: blocks.whoTitle, paragraphs: blocks.whoParagraphs ?? [] },
  { id: "about-how", icon: <Clock3 />, title: blocks.howTitle, bullets: blocks.howBullets ?? [] },
  {
    id: "about-compliance",
    icon: <ShieldCheck />,
    title: blocks.complianceTitle,
    paragraphs: [...(blocks.complianceParagraphs ?? []), blocks.complianceNote].filter(Boolean)
  },
  { id: "about-contact", icon: <Mail />, title: blocks.contactTitle, paragraphs: [blocks.contactBody].filter(Boolean), cta: blocks.contactCtaLabel, href: blocks.contactCtaHref }];

  return (
    <section className="about-overview" aria-labelledby="about-overview-heading">
      <div className="about-overview-inner">
        <nav aria-label={uiText("about.text_e99da2e58c")} className="about-breadcrumb">
          <Link to="/">{uiText("about.text_e9a696e9a1")}</Link>
          <span aria-hidden="true">/</span>
          <span>{uiText("about.cic_3")}</span>
        </nav>
        <p className="about-kicker">{uiText("about.about_kicker_overview")}</p>
        <p id="about-overview-heading" className="about-overview-intro">{content.about.paragraphs?.[0]}</p>
        <nav aria-label={uiText("about.text_e69cace9a1")} className="about-section-nav">
          <p>{uiText("about.text_e69cace9a1_2")}</p>
          <ul>
            {navItems.map((item) =>
            <li key={item.id}>
                <a href={`#${item.id}`}>
                  {item.label}
                  <ChevronRight aria-hidden="true" />
                </a>
              </li>
            )}
          </ul>
        </nav>
        <div className="about-eeat-cards">
          {items.map((item, index) =>
          <article id={item.id} className="about-eeat-card" key={item.title}>
              <div className="about-card-index">
                <span>{String(index + 1).padStart(2, "0")}</span>
                <div aria-hidden="true">{item.icon}</div>
              </div>
              <div className="about-card-body">
                <h2>{item.title}</h2>
                {item.paragraphs?.map((line) => <p key={line}>{line}</p>)}
                {item.bullets?.length > 0 &&
              <ul>
                    {item.bullets.map((line) =>
                <li key={line}><CheckCircle2 aria-hidden="true" /> <span>{line}</span></li>
                )}
                  </ul>
              }
                {item.cta && <Link className="about-inline-cta" to={item.href || "/contact"}>{item.cta}<ArrowRight aria-hidden="true" /></Link>}
              </div>
            </article>
          )}
        </div>
      </div>
    </section>);

}

function AboutSection({ preview = false }) {
  const about = content.about;
  if (preview) return <AboutPreviewSection about={about} />;

  return (
    <>
      <section id="about-mission" className="about-mission-section" aria-labelledby="about-mission-heading">
        <div className="about-mission-inner">
          <div className="about-image-panel">
            <img src={about.coverImage} alt={about.title || "CIC"} className="about-photo-neutral" loading="eager" width="800" height="600" />
          </div>
          <div className="about-mission-copy">
            <p className="about-kicker">{uiText("about.about_kicker_mission")}</p>
            <h2 id="about-mission-heading">{about.missionPageTitle}</h2>
            <div className="about-text-flow">
              {(about.paragraphs ?? []).map((p) => <p key={p}>{p}</p>)}
            </div>
            <div className="about-stat-tiles">
              <Stat value="24/7" label={about.stats.response} />
              <Stat value={`${about.statNumbers.countriesTarget}+`} label={about.stats.countries} />
              <Stat value="100%" label={about.stats.confidentiality || uiText("about.text_e4bf9de5af")} />
              <Stat value={`${about.statNumbers.lawyersTarget}+`} label={about.stats.lawyers} />
            </div>
          </div>
        </div>
      </section>
      <section id="about-values" className="about-values-section" aria-labelledby="about-values-heading">
        <div className="about-values-inner">
          <div className="about-section-head">
            <p className="about-kicker">{uiText("about.about_kicker_values")}</p>
            <h2 id="about-values-heading">{about.coreValues.title}</h2>
            {about.coreValues.subtitle && <p>{about.coreValues.subtitle}</p>}
          </div>
          <div className="about-values-grid">
            {(about.coreValues.items ?? []).map((item, index) =>
            <article className="about-value-card" key={item.title}>
                <div className="about-value-icon" aria-hidden="true">
                  {[<Shield />, <Earth />, <Users />, <Award />][index] || <BadgeCheck />}
                </div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </article>
            )}
          </div>
        </div>
      </section>
      <ProfessionalIntro />
      <section className="about-consult-band" aria-labelledby="about-consult-heading">
        <div className="about-consult-inner">
          <div>
            <h2 id="about-consult-heading">{about.consultBanner.title}</h2>
            <p>{about.consultBanner.subtitle}</p>
          </div>
          <Link to={about.consultBanner.ctaHref || "/contact"}>{about.consultBanner.cta}</Link>
        </div>
      </section>
    </>);

}

function AboutPreviewSection({ about }) {
  const statItems = [
  { value: "24/7", label: about.stats.response, icon: <Clock3 size={20} /> },
  { value: `${about.statNumbers.countriesTarget}+`, label: about.stats.countries, icon: <Globe2 size={20} /> },
  { value: "100%", label: about.stats.confidentiality || uiText("about.text_e6b787e6bf"), icon: <ShieldCheck size={20} /> },
  { value: `${about.statNumbers.lawyersTarget}+`, label: about.stats.lawyers, icon: <Users size={20} /> }];


  return (
    <section id="about" className="apple-section anli-silver-section overflow-hidden border-t border-neutral-200/55">
      <div className="max-w-7xl mx-auto">
        <div className="anli-about-grid transition-all duration-700 opacity-100 translate-y-0">
          <div className="relative group">
            <div className="anli-about-image-wrap">
              <img
                src={about.coverImage}
                alt={uiText("about.cic_4")}
                className="about-photo-neutral h-full w-full object-cover transition-transform duration-700 ease-out"
                loading="lazy" />
              
            </div>
            <div className="anli-about-gold-block" />
          </div>
          <div>
            <div className="section-heading-decorated mb-6">
              <h2 className="anli-section-title">{about.title}</h2>
            </div>
            <div className="space-y-4">
              {about.paragraphs.map((paragraph, index) =>
              <p key={paragraph} className={`anli-body-text${index === 0 ? " text-foreground-80" : ""}`}>
                  {paragraph}
                </p>
              )}
            </div>
            <div className="anli-about-stats">
              {statItems.map((item) =>
              <div key={item.label} className="card-gradient">
                  <div className="relative flex items-start gap-3">
                    <div className="p-2 rounded-lg gold-bg-subtle gold-text">{item.icon}</div>
                    <div>
                      <div className="anli-stat-value tabular-nums">{item.value}</div>
                      <div className="anli-stat-label">{item.label}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <Link to="/about" className="inline-block mt-8 anli-btn-dark click-bounce">
              {about.cta}
            </Link>
          </div>
        </div>
      </div>
    </section>);

}

function ProfessionalIntro() {
  const intro = content.about.professionalIntro;
  if (!intro) return null;
  return (
    <section id="about-team-intro" className="about-team-intro" aria-labelledby="about-team-intro-heading">
      <div className="about-team-copy">
        <p className="about-kicker">{uiText("about.about_kicker_team")}</p>
        <h2 id="about-team-intro-heading">{intro.title}</h2>
        <div className="about-text-flow">
          {intro.paragraphs.map((p) => <p key={p}>{p}</p>)}
        </div>
        <Link className="about-inline-cta" to="/team">{uiText("about.text_e69fa5e79c")} <ArrowRight aria-hidden="true" /></Link>
      </div>
      <div className="about-image-panel">
        <img src={intro.imageUrl || content.about.coverImage} alt={intro.title || "CIC"} className="about-photo-neutral" loading="lazy" width="800" height="600" />
      </div>
    </section>);

}

function AdvantagesPage({ mode }) {
  const adv = content.advantages;
  const title = mode === "services" ? adv.servicesSeoH1 : adv.seoH1;
  useSeo(mode === "services" ? adv.servicesBrowserTitle : adv.browserTitle, mode === "services" ? adv.servicesBrowserDescription : adv.browserDescription);
  return (
    <PageShell title={title} subtitle={adv.subtitle}>
      <AdvantagesSection />
      <ContentAttribution />
    </PageShell>);

}

function AdvantagesSection({ preview = false }) {
  const adv = content.advantages;
  const items = adv.items ?? [];
  const [visible, setVisible] = React.useState(false);
  const [statsVisible, setStatsVisible] = React.useState(false);
  const sectionRef = React.useRef(null);
  const statsRef = React.useRef(null);
  const isPage = !preview;
  const countValues = [
  useCountUp(items[0]?.stat ?? 15, 2000, visible),
  useCountUp(items[1]?.stat ?? 93, 2000, visible),
  useCountUp(items[2]?.stat ?? 100, 2000, visible)];

  const cards = items.slice(0, 3).map((item, index) => ({
    ...item,
    stat: countValues[index],
    icon: [Award, TrendingUp, ShieldCheck][index] || BadgeCheck
  }));
  const stats = (content.network.globalStats ?? []).slice(0, 4);
  const statCounts = [
  useCountUp(stats[0]?.value ?? 0, 2200, statsVisible && isPage),
  useCountUp(stats[1]?.value ?? 0, 2200, statsVisible && isPage),
  useCountUp(stats[2]?.value ?? 0, 2200, statsVisible && isPage),
  useCountUp(stats[3]?.value ?? 0, 2200, statsVisible && isPage)];


  React.useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setVisible(true);
    }, { threshold: 0.1 });
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    if (!isPage) return;
    const node = statsRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setStatsVisible(true);
    }, { threshold: 0.12 });
    observer.observe(node);
    return () => observer.disconnect();
  }, [isPage]);

  const section =
  <section id="advantages" ref={sectionRef} className="apple-section anli-light-section">
      <div className="max-w-7xl mx-auto section-padding">
        {preview ?
      <div className={`text-center mb-10 sm:mb-14 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="section-heading-decorated mb-3 sm:mb-4">
              <h2 className="anli-section-title">{adv.title}</h2>
            </div>
            <p className="anli-section-subtitle">{adv.subtitle}</p>
          </div> :

      adv.pageIntro &&
      <p className={`max-w-3xl mx-auto text-center text-[14px] sm:text-[15px] text-neutral-700 leading-relaxed mb-10 sm:mb-12 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              {adv.pageIntro}
            </p>

      }

        <div className="grid md:grid-cols-3 gap-4 sm:gap-6">
          {cards.map((item, index) => {
          const Icon = item.icon;
          return (
            <div
              className={`rounded-xl border border-neutral-200/90 bg-white p-5 sm:p-8 text-center shadow-sm transition-all duration-500 hover:shadow-md ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              style={{ transitionDelay: `${200 + 150 * index}ms` }}
              key={item.title}>
              
                <div className="mx-auto mb-3 sm:mb-4 flex h-11 w-11 sm:h-12 sm:w-12 items-center justify-center rounded-full border border-neutral-200 bg-neutral-50">
                  <Icon className="h-6 w-6 text-neutral-700" strokeWidth={1.5} />
                </div>
                <div className="flex flex-wrap items-baseline justify-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                  <span className="font-sans text-3xl sm:text-4xl font-bold text-neutral-900 tabular-nums">
                    {item.stat}
                    {item.suffix}
                  </span>
                  <span className="text-xs sm:text-sm text-neutral-500">{item.statLabel}</span>
                </div>
                <h3 className="mb-2.5 sm:mb-3 font-sans text-[1.02rem] sm:text-xl font-bold text-neutral-900">{item.title}</h3>
                <p className="anli-body-text text-left sm:text-center">{item.desc}</p>
                {isPage && item.bullets?.length > 0 &&
              <ul className="mt-4 text-left space-y-2 border-t border-neutral-100 pt-4">
                    {item.bullets.map((bullet) =>
                <li className="flex gap-2 text-[12.5px] sm:text-[13px] text-neutral-700 leading-snug" key={bullet}>
                        <span className="text-emerald-600 font-bold shrink-0" aria-hidden="true">✓</span>
                        <span>{bullet}</span>
                      </li>
                )}
                  </ul>
              }
              </div>);

        })}
        </div>

        {isPage && adv.moreItems?.length > 0 &&
      <div className="mt-14 sm:mt-16 border-t border-neutral-200/80 pt-12 sm:pt-14">
            <div className="text-center mb-8 sm:mb-10">
              <h2 className="anli-section-title">{adv.moreTitle}</h2>
              {adv.moreSubtitle && <p className="anli-section-subtitle mt-2.5">{adv.moreSubtitle}</p>}
            </div>
            <div className="grid md:grid-cols-3 gap-4 sm:gap-6">
              {adv.moreItems.map((item, index) => {
            const MoreIcon = [Clock3, Earth, ListChecks][index] || CheckCircle2;
            return (
              <div className="rounded-xl border border-neutral-200/90 bg-white p-6 shadow-sm" key={item.title}>
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-neutral-900/5 mb-3">
                    <MoreIcon className="h-5 w-5 text-neutral-800" aria-hidden="true" />
                  </div>
                  <h3 className="font-bold text-neutral-900 text-[15px] sm:text-base mb-2">{item.title}</h3>
                  <p className="text-[13px] sm:text-[14px] text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>);
          })}
            </div>
          </div>
      }

        {isPage && stats.length > 0 && adv.statsTitle &&
      <div ref={statsRef} className="mt-14 sm:mt-16 border-t border-neutral-200/80 pt-12 sm:pt-14">
            <div className="text-center mb-8 sm:mb-10">
              <h2 className="anli-section-title">{adv.statsTitle}</h2>
              {adv.statsSubtitle && <p className="anli-section-subtitle mt-2.5">{adv.statsSubtitle}</p>}
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {stats.map((item, index) =>
          <div className="rounded-xl border border-neutral-200/90 bg-white px-4 py-6 sm:py-8 text-center shadow-sm" key={item.label}>
                  <div className="font-sans text-2xl sm:text-3xl lg:text-4xl font-bold text-neutral-900 tabular-nums">
                    {statCounts[index]}
                    {item.suffix}
                  </div>
                  <div className="mt-1.5 text-xs sm:text-sm text-neutral-600">{item.label}</div>
                </div>
          )}
            </div>
          </div>
      }

        <div className={`mt-8 sm:mt-12 anli-inline-cta-panel px-5 sm:px-10 py-6 sm:py-10 text-center transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{ transitionDelay: "650ms" }}>
          <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-amber-500/10">
            <Zap className="h-5 w-5 text-amber-600" aria-hidden="true" />
          </div>
          <p className="text-[1.02rem] sm:text-lg font-sans font-bold text-neutral-900 mb-1.5 sm:mb-2">{adv.ctaTitle}</p>
          <p className="text-neutral-600 text-[13px] sm:text-[15px] mb-5 sm:mb-6 max-w-xl mx-auto leading-[1.65]">{adv.ctaSubtitle}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            {!isPage ?
          <>
                <Link className="anli-btn-neutral click-bounce w-full sm:w-auto justify-center" to="/advantages">{adv.ctaPrimary}</Link>
                <button type="button" className="anli-btn-dark click-bounce inline-flex w-full sm:w-auto px-8 justify-center" onClick={openCustomerService}>{adv.ctaSecondary}</button>
              </> :

          <>
                <Link className="anli-btn-neutral click-bounce w-full sm:w-auto justify-center" to="/process">{uiText("process.text_e4ba86e8a7")}</Link>
                <button type="button" className="anli-btn-dark click-bounce inline-flex w-full sm:w-auto px-8 justify-center" onClick={openCustomerService}>{adv.ctaSecondary}</button>
              </>
          }
          </div>
        </div>

        {isPage && adv.pageBottomTitle &&
      <div className="anli-dark-band mt-10 sm:mt-12 rounded-2xl px-6 sm:px-12 py-10 sm:py-12 text-center shadow-lg">
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight">{adv.pageBottomTitle}</h2>
            {adv.pageBottomSubtitle &&
        <p className="mt-3 text-[13px] sm:text-[15px] text-gray-400 max-w-2xl mx-auto leading-relaxed">{adv.pageBottomSubtitle}</p>
        }
            <div className="mt-6 flex justify-center">
              <button type="button" className="anli-btn-dark click-bounce inline-flex px-8 justify-center" onClick={openCustomerService}>{adv.pageBottomCtaText}</button>
            </div>
          </div>
      }
      </div>
    </section>;


  if (preview) {
    return (
      <div
        className="transition-all duration-700 ease-out motion-reduce:transition-none opacity-100 translate-y-0 motion-reduce:opacity-100 motion-reduce:translate-y-0"
        style={{ transitionDelay: "200ms" }}>
        
        {section}
      </div>);

  }

  return section;
}

function ProcessPage({ mode }) {
  const process = content.process;
  useSeo(mode === "how-it-works" ? process.howItWorksSeoH1 : process.seoH1, process.pageIntro);
  return (
    <PageShell title={mode === "how-it-works" ? process.howItWorksSeoH1 : process.seoH1} subtitle={process.subtitle}>
      <ProcessSection />
      <ContentAttribution />
    </PageShell>);

}

const processIcons = [ClipboardCheck, FilePenLine, Radar, Banknote];

function ProcessSection({ preview = false }) {
  const process = content.process;
  const navigate = useNavigate();
  const [visible, setVisible] = React.useState(false);
  const [active, setActive] = React.useState(0);
  const sectionRef = React.useRef(null);
  const isPage = !preview;
  const pageIntro = (process.pageIntro ?? "").trim();
  const timelinePhases = process.timelinePhases ?? [];
  const steps = (process.steps ?? []).map((step, index) => ({ ...step, icon: processIcons[index] ?? Banknote }));

  React.useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setVisible(true);
    }, { threshold: 0.1 });

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const section =
  <section id="process" ref={sectionRef} className="apple-section surface-elevated overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {!isPage &&
      <div className={`text-center mb-10 sm:mb-14 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="section-heading-decorated mb-3 sm:mb-4">
              <h2 className="anli-section-title">{process.title}</h2>
            </div>
            <p className="anli-section-subtitle">{process.subtitle}</p>
          </div>
      }

        {isPage && pageIntro ?
      <p className={`max-w-3xl mx-auto text-center text-[14px] sm:text-[15px] text-muted-foreground leading-relaxed mb-10 sm:mb-12 section-padding pt-6 sm:pt-8 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"} transition-all duration-700`}>
            {pageIntro}
          </p> :
      null}

        <div className={`relative mb-9 sm:mb-12 max-w-5xl mx-auto px-2 transition-all duration-700 ${visible ? "opacity-100" : "opacity-0"}`} style={{ transitionDelay: "200ms" }}>
          <div className="hidden md:block absolute left-[10%] right-[10%] top-1/2 h-px -translate-y-1/2 bg-gradient-to-r from-transparent via-border to-transparent pointer-events-none" aria-hidden="true" />
          <div className="relative flex flex-wrap justify-center md:justify-between gap-3 sm:gap-4 md:gap-2">
            {steps.map((step, index) =>
          <button
            type="button"
            onClick={() => setActive(index)}
            className={`click-bounce relative z-10 flex h-10 w-10 sm:h-11 sm:w-11 shrink-0 items-center justify-center rounded-full font-sans text-sm font-bold transition-all duration-300 md:h-12 md:w-12 ${active === index ? "gold-bg text-primary-foreground shadow-md shadow-amber-900/15 ring-2 ring-amber-400/35 ring-offset-2 ring-offset-white" : "border border-border bg-white text-muted-foreground hover:border-amber-500/40 hover:text-foreground"}`}
            key={step.num}>
            
                {index + 1}
              </button>
          )}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 lg:gap-4">
          {steps.map((step, index) => {
          const Icon = step.icon;
          return (
            <div
              role="button"
              tabIndex={0}
              onClick={() => setActive(index)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setActive(index);
                }
              }}
              className={`relative card-gradient cursor-pointer p-5 sm:p-6 outline-none transition-all duration-500 focus-visible:ring-2 focus-visible:ring-amber-500/40 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${active === index ? "border-amber-500/30 shadow-md shadow-amber-900/8 ring-2 ring-amber-500/35" : ""}`}
              style={{ transitionDelay: `${300 + 120 * index}ms` }}
              key={step.num}>
              
                <div className="flex items-center justify-between mb-3.5 sm:mb-4">
                  <div className={`rounded-lg p-2 sm:p-2.5 transition-colors duration-300 ${active === index ? "gold-bg" : "gold-bg-subtle"}`}>
                    <Icon className={`h-5 w-5 sm:h-6 sm:w-6 transition-colors duration-300 ${active === index ? "text-primary-foreground" : "gold-text"}`} />
                  </div>
                  <span className="font-sans text-2xl font-bold text-foreground/[0.09] sm:text-3xl">{step.num}</span>
                </div>
                <h3 className="mb-2 font-sans text-[1.02rem] sm:text-lg font-bold text-neutral-900">{step.title}</h3>
                <p className="anli-body-text">{step.desc}</p>
                {isPage && step.bullets?.length > 0 ?
              <ul className="mt-3 pt-3 border-t border-border/60 space-y-1.5 text-left">
                    {step.bullets.map((bullet) =>
                <li className="flex gap-2 text-[11.5px] sm:text-[12.5px] text-muted-foreground leading-snug" key={bullet}>
                        <span className="text-emerald-600 font-bold shrink-0" aria-hidden="true">✓</span>
                        <span>{bullet}</span>
                      </li>
                )}
                  </ul> :
              null}
                {index < steps.length - 1 &&
              <div className="pointer-events-none absolute right-0 top-1/2 z-0 hidden h-0.5 w-10 max-w-[42%] -translate-y-1/2 translate-x-1/2 bg-gradient-to-r from-amber-500/45 to-transparent lg:block" aria-hidden="true" />
              }
              </div>);

        })}
        </div>

        {!isPage ?
      <div className={`mt-8 sm:mt-12 text-center transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0"}`} style={{ transitionDelay: "800ms" }}>
            <p className="mx-auto mb-4 sm:mb-5 max-w-xl text-[13px] sm:text-sm leading-relaxed text-muted-foreground">{process.hint}</p>
            <div className="flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
              <Link to="/process" className="anli-btn-neutral click-bounce px-8">{uiText("process.text_e69fa5e79c_2")}</Link>
              <button type="button" className="anli-btn-dark click-bounce px-8" onClick={openCustomerService}>{process.cta}</button>
            </div>
          </div> :
      null}

        {isPage && timelinePhases.length > 0 && (process.timelineTitle ?? "").length > 0 ?
      <div className="mt-12 sm:mt-16 border-t border-neutral-200/80 bg-neutral-50/90">
            <div className="max-w-7xl mx-auto section-padding py-12 sm:py-14">
              <div className="text-center mb-8 sm:mb-10">
                <h2 className="anli-section-title">{process.timelineTitle}</h2>
                {process.timelineSubtitle ? <p className="anli-section-subtitle mt-2.5">{process.timelineSubtitle}</p> : null}
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
                {timelinePhases.map((phase) =>
            <div className="rounded-xl border border-neutral-200/90 bg-white p-5 shadow-sm" key={`${phase.range}${phase.title}`}>
                    <p className="text-xs font-bold uppercase tracking-wide text-amber-700/90">{phase.range}</p>
                    <h3 className="mt-1.5 font-bold text-neutral-900 text-[15px] sm:text-base leading-snug">{phase.title}</h3>
                    <ul className="mt-3 space-y-2">
                      {phase.items.map((item) =>
                <li className="flex gap-2 text-[12px] sm:text-[13px] text-muted-foreground leading-relaxed" key={item}>
                          <span className="text-neutral-400 shrink-0" aria-hidden="true">·</span>
                          <span>{item}</span>
                        </li>
                )}
                    </ul>
                  </div>
            )}
              </div>
            </div>
          </div> :
      null}

        {isPage && process.processFaqItems?.length > 0 ?
      <div className="border-t border-neutral-200/80">
            <FAQSection title={process.processFaqTitle ?? content.faq.title} subtitle={process.processFaqSubtitle ?? content.faq.subtitle} items={process.processFaqItems} />
          </div> :
      null}

        {isPage && (process.pageBottomTitle ?? "").trim() ?
      <div className="section-padding pb-12 sm:pb-16">
            <div className="anli-dark-band max-w-4xl mx-auto rounded-2xl px-6 sm:px-12 py-10 sm:py-12 text-center shadow-lg">
              <h2 className="text-xl sm:text-2xl font-bold tracking-tight">{process.pageBottomTitle}</h2>
              {process.pageBottomSubtitle ? <p className="mt-3 text-[13px] sm:text-[15px] text-gray-400 max-w-2xl mx-auto leading-relaxed">{process.pageBottomSubtitle}</p> : null}
              <div className="mt-6 flex justify-center">
                <button type="button" className="anli-btn-dark inline-flex px-8 py-3" onClick={openCustomerService}>{(process.pageBottomCtaText ?? process.cta).trim()}</button>
              </div>
            </div>
          </div> :
      null}
      </div>
    </section>;


  if (!preview) return section;

  return (
    <div className="transition-all duration-700 ease-out motion-reduce:transition-none opacity-100 translate-y-0 motion-reduce:opacity-100 motion-reduce:translate-y-0" style={{ transitionDelay: "200ms" }}>
      {section}
    </div>);

}

function TeamPage() {
  const team = content.team;
  useSeo(team.browserTitle, team.browserDescription);
  return (
    <PageShell title={team.seoH1} subtitle={team.pageIntro}>
      <TeamSection />
      <ContentAttribution />
    </PageShell>);

}

function TeamSection({ preview = false }) {
  const team = content.team;
  const [visible, setVisible] = React.useState(false);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(null);
  const [fadeState, setFadeState] = React.useState("in");
  const [copied, setCopied] = React.useState(false);
  const touchStartX = React.useRef(null);
  const sectionRef = React.useRef(null);
  const variant = preview ? "home" : "subpage";
  const members = React.useMemo(() => {
    const list = (team.members ?? []).map((member, index) => ({
      ...member,
      img: member.imageUrl?.trim() || teamImageByIndex[index] || teamFounderImage
    }));
    if (variant !== "subpage") return list;
    const lawyers = list.filter((member) => memberGroup(member) === "lawyer");
    const investigators = list.filter((member) => memberGroup(member) === "investigator");
    return investigators.length ? [...lawyers, ...investigators] : list;
  }, [team.members, variant]);
  const lawyers = React.useMemo(() => members.filter((member) => memberGroup(member) === "lawyer"), [members]);
  const investigators = React.useMemo(() => members.filter((member) => memberGroup(member) === "investigator"), [members]);
  const count = members.length;
  const normalizedIndex = activeIndex === null || !count ? null : (activeIndex % count + count) % count;
  const activeMember = normalizedIndex === null ? null : members[normalizedIndex] ?? null;
  const featured = variant === "home" ? members.slice(0, 2) : [];
  const rest = variant === "home" ? members.slice(2) : [];

  React.useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setVisible(true);
    }, { threshold: 0.08 });
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const setDialogUrl = React.useCallback((index, open = dialogOpen) => {
    const url = new URL(window.location.href);
    if (index !== null && open && members[index]) {
      url.searchParams.set("team", memberKey(members[index], `${index + 1}`));
    } else {
      url.searchParams.delete("team");
    }
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  }, [dialogOpen, members]);

  const openMember = (index) => {
    setActiveIndex(index);
    setDialogOpen(true);
    setFadeState("in");
  };

  const switchMember = React.useCallback((index) => {
    if (!count) return;
    setFadeState("out");
    window.setTimeout(() => {
      setActiveIndex(index);
      setFadeState("in");
    }, 110);
  }, [count]);

  const previousMember = React.useCallback(() => {
    if (!count) return;
    switchMember(((normalizedIndex ?? 0) - 1 + count) % count);
  }, [count, normalizedIndex, switchMember]);

  const nextMember = React.useCallback(() => {
    if (!count) return;
    switchMember(((normalizedIndex ?? 0) + 1) % count);
  }, [count, normalizedIndex, switchMember]);

  React.useEffect(() => {
    if (!dialogOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setDialogOpen(false);
        setActiveIndex(null);
        setDialogUrl(null, false);
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        previousMember();
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        nextMember();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [dialogOpen, nextMember, previousMember, setDialogUrl]);

  React.useEffect(() => {
    if (dialogOpen && normalizedIndex !== null) setDialogUrl(normalizedIndex, true);
  }, [dialogOpen, normalizedIndex, setDialogUrl]);

  React.useEffect(() => {
    const applyUrlMember = () => {
      const value = new URL(window.location.href).searchParams.get("team");
      if (!value || !members.length) {
        setDialogOpen(false);
        setActiveIndex(null);
        return;
      }
      const decoded = decodeURIComponent(value.trim());
      const index = members.findIndex((member) => member.slug?.trim() === decoded || memberKey(member) === decoded);
      if (index >= 0) {
        setActiveIndex(index);
        setDialogOpen(true);
        setFadeState("in");
      }
    };
    applyUrlMember();
    window.addEventListener("popstate", applyUrlMember);
    return () => window.removeEventListener("popstate", applyUrlMember);
  }, [members]);

  const closeDialog = () => {
    setDialogOpen(false);
    setActiveIndex(null);
    setDialogUrl(null, false);
  };

  const renderCard = (member, index, large = false) =>
  <button
    type="button"
    className={`group relative rounded-xl border border-neutral-200/90 bg-white px-3.5 sm:px-5 pb-4.5 sm:pb-5 pt-6 sm:pt-8 text-center shadow-sm transition-all duration-700 hover:shadow-md ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
    style={{ transitionDelay: `${200 + 90 * index}ms` }}
    onClick={() => openMember(index)}
    key={member.slug?.trim() || `${index}-${member.name}`}>
    
      <div className={`relative mx-auto mb-4 ${large ? "h-32 w-32 sm:h-40 sm:w-40" : "h-24 w-24 sm:h-[7.5rem] sm:w-[7.5rem]"}`}>
        <img
        src={member.img}
        alt={member.name}
        className="about-photo-neutral h-full w-full rounded-full object-cover object-top shadow-inner ring-2 ring-neutral-100 transition-transform duration-500 group-hover:scale-[1.02]"
        loading="lazy" />
      
        <div className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-emerald-500 ring-2 ring-white" aria-hidden="true" />
      </div>
      <div className="mb-2.5 flex justify-center">
        <span className="rounded-full bg-neutral-900 px-3.5 py-1.5 text-[10px] sm:text-[11px] font-semibold tracking-wide text-white">{member.role}</span>
      </div>
      <h3 className="font-sans text-base sm:text-lg font-bold tracking-tight text-neutral-900">{member.name}</h3>
      <p className="mt-2 text-[12px] sm:text-[13px] text-neutral-600">{member.exp}</p>
      <p className="mt-1 text-[11px] sm:text-xs leading-relaxed text-neutral-500">{member.edu}</p>
      <div className="mt-3 flex items-center justify-center gap-0.5 text-[10px] sm:text-xs text-neutral-500">
        <span>{uiText("team.text_e782b9e587")}</span>
        <ChevronRight className="h-3.5 w-3.5 opacity-70" />
        <ChevronRight className="-ml-1.5 h-3.5 w-3.5 opacity-70" />
      </div>
    </button>;


  return (
    <section id="team" ref={sectionRef} className="apple-section anli-light-section">
      <div className="max-w-7xl mx-auto">
        {variant === "subpage" ?
        <div className={`section-padding pb-4 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <section className="mb-12 sm:mb-14" aria-labelledby="team-lawyers-heading">
              <h2 id="team-lawyers-heading" className="font-sans text-2xl font-bold tracking-tight text-neutral-900 sm:text-3xl">
                {team.lawyerSectionTitle ?? uiText("team.text_e5be8be5b8")}
              </h2>
              <div className="mt-6 space-y-8">
                {lawyers.map((member, index) =>
              <article className="rounded-xl border border-neutral-200/90 bg-white p-5 shadow-sm sm:p-6" key={member.slug?.trim() || `${member.name}-lawyer`}>
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                      <img src={member.img} alt="" className="about-photo-neutral h-24 w-24 shrink-0 rounded-full object-cover object-top ring-2 ring-neutral-100 sm:h-28 sm:w-28" loading="lazy" />
                      <div className="min-w-0 flex-1">
                        <h3 className="font-sans text-lg font-bold text-neutral-900 sm:text-xl">{member.name}</h3>
                        <p className="mt-2 text-[14px] leading-relaxed text-neutral-700 sm:text-[15px]">{uiText("team.text_e689a7e4b8")}
                      {member.practiceCredential?.trim() || uiText("team.text_e6ada3e5bc")}
                        </p>
                        <p className="mt-2 text-[14px] leading-relaxed text-neutral-700 sm:text-[15px]">{uiText("team.text_e4b893e4b8")}
                      {member.expertise?.trim() || uiText("team.text_e8b7a8e5a2")}
                        </p>
                        <p className="mt-2 text-[14px] leading-relaxed text-neutral-700 sm:text-[15px]">{uiText("team.text_e689a7e4b8_2")}
                      {memberPracticeYears(member)}
                        </p>
                        <button type="button" className="mt-4 text-sm font-medium text-amber-800 underline underline-offset-4 hover:text-amber-900" onClick={() => openMember(index)}>{uiText("team.text_e69fa5e79c_3")}

                    </button>
                      </div>
                    </div>
                  </article>
              )}
              </div>
            </section>
            {investigators.length > 0 &&
          <section className="mb-10" aria-labelledby="team-inv-heading">
                <h2 id="team-inv-heading" className="font-sans text-2xl font-bold tracking-tight text-neutral-900 sm:text-3xl">
                  {team.investigatorSectionTitle ?? uiText("team.text_e8b083e69f")}
                </h2>
                <div className="mt-6 space-y-8">
                  {investigators.map((member, index) => {
                const memberIndex = lawyers.length + index;
                return (
                  <article className="rounded-xl border border-neutral-200/90 bg-white p-5 shadow-sm sm:p-6" key={member.slug?.trim() || `${member.name}-inv`}>
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                          <img src={member.img} alt="" className="about-photo-neutral h-24 w-24 shrink-0 rounded-full object-cover object-top ring-2 ring-neutral-100 sm:h-28 sm:w-28" loading="lazy" />
                          <div className="min-w-0 flex-1">
                            <h3 className="font-sans text-lg font-bold text-neutral-900 sm:text-xl">{member.name}</h3>
                            <p className="mt-2 text-[14px] leading-relaxed text-neutral-700 sm:text-[15px]">{uiText("team.text_e4b893e4b8_2")}
                          {member.practiceCredential?.trim() || uiText("team.text_e6ada3e5bc_2")}
                            </p>
                            <p className="mt-2 text-[14px] leading-relaxed text-neutral-700 sm:text-[15px]">{uiText("team.text_e4b893e4b8")}
                          {member.expertise?.trim() || uiText("team.text_e58cbae59d")}
                            </p>
                            <button type="button" className="mt-4 text-sm font-medium text-amber-800 underline underline-offset-4 hover:text-amber-900" onClick={() => openMember(memberIndex)}>{uiText("team.text_e69fa5e79c_3")}

                        </button>
                          </div>
                        </div>
                      </article>);

              })}
                </div>
              </section>
          }
          </div> :

        <>
            <div className={`text-center mb-10 sm:mb-12 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <h2 className="anli-section-title">{team.title}</h2>
              <p className="anli-section-subtitle mt-2.5 sm:mt-3">{team.subtitle}</p>
            </div>
            <div className={featured.length === 1 ? "mx-auto max-w-md" : "grid gap-4 sm:gap-6 sm:grid-cols-2"}>
              {featured.map((member, index) => renderCard(member, index, true))}
            </div>
            {rest.length > 0 &&
          <div className="mt-4 sm:mt-6 grid gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {rest.map((member, index) => renderCard(member, index + featured.length, false))}
              </div>
          }
          </>
        }
        {team.eeatFootnote?.trim() &&
        <p className="mx-auto mt-8 max-w-3xl px-2 text-center text-[11px] sm:text-xs leading-relaxed text-neutral-500">{team.eeatFootnote.trim()}</p>
        }
        {dialogOpen &&
        <div className="team-dialog-backdrop" role="dialog" aria-modal="true" aria-label={activeMember?.name || uiText("team.text_e59ba2e998_2")} onMouseDown={closeDialog}>
            <div className="team-dialog max-w-2xl p-0 overflow-hidden rounded-xl border-border/80" onMouseDown={(event) => event.stopPropagation()}>
              <button type="button" className="team-dialog-close" aria-label={uiText("team.text_e585b3e997")} onClick={closeDialog}>
                <X className="h-5 w-5" />
              </button>
              {activeMember ?
            <div className={`grid md:grid-cols-[240px_1fr] transition-opacity duration-150 ${fadeState === "out" ? "opacity-0" : "opacity-100"}`}>
                  <div className="relative bg-black">
                    <img src={activeMember.img} alt={activeMember.name} className="about-photo-neutral h-full w-full object-cover object-top" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="inline-flex items-center gap-2 rounded-full border border-amber-400/35 bg-black/30 px-3 py-1 text-[11px] font-semibold tracking-wide text-amber-200 backdrop-blur-md">{activeMember.role}</div>
                      <div className="mt-2 font-sans text-xl font-bold text-white tracking-tight">{activeMember.name}</div>
                    </div>
                    <button type="button" onClick={previousMember} className="team-dialog-nav team-dialog-nav-left" aria-label={uiText("team.text_e4b88ae4b8")}>
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button type="button" onClick={nextMember} className="team-dialog-nav team-dialog-nav-right" aria-label={uiText("team.text_e4b88be4b8")}>
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </div>
                  <div
                className="p-6 md:p-7"
                onTouchStart={(event) => {
                  touchStartX.current = event.touches[0]?.clientX ?? null;
                }}
                onTouchEnd={(event) => {
                  const start = touchStartX.current;
                  if (start === null) return;
                  const distance = (event.changedTouches[0]?.clientX ?? start) - start;
                  if (Math.abs(distance) >= 40) {
                    if (distance > 0) previousMember();else
                    nextMember();
                  }
                  touchStartX.current = null;
                }}>
                
                    <div className="space-y-3">
                      <div>
                        <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{uiText("team.text_e7bb8fe9aa")}</div>
                        <div className="mt-1 text-sm text-foreground/80 leading-relaxed">{activeMember.exp}</div>
                      </div>
                      <div>
                        <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{uiText("team.text_e69599e882")}</div>
                        <div className="mt-1 text-sm text-foreground/75 leading-relaxed">{activeMember.edu}</div>
                      </div>
                      {activeMember.bio?.trim() &&
                  <div>
                          <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{uiText("team.text_e8afa6e683")}</div>
                          <div className="mt-2 anli-body-text text-foreground/70">{activeMember.bio}</div>
                        </div>
                  }
                    </div>
                    <div className="mt-6 flex flex-col gap-3">
                      <div className="flex flex-col sm:flex-row gap-3">
                        <Link to="/contact" className="anli-btn-dark click-bounce w-full sm:w-auto text-center" onClick={closeDialog}>{uiText("team.text_e9a284e7ba")}

                    </Link>
                        <a href="#process" className="anli-btn-neutral click-bounce w-full sm:w-auto text-center" onClick={closeDialog}>{uiText("team.text_e4ba86e8a7_2")}

                    </a>
                      </div>
                      <div className="flex items-center gap-2 sm:hidden">
                        <button type="button" onClick={previousMember} className="team-mobile-switch"><ChevronLeft className="h-4 w-4" />{uiText("team.text_e4b88ae4b8")}</button>
                        <button type="button" onClick={nextMember} className="team-mobile-switch">{uiText("team.text_e4b88be4b8")}<ChevronRight className="h-4 w-4" /></button>
                      </div>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <div className="hidden sm:flex items-center gap-2">
                          <span className="inline-flex items-center gap-1 rounded border border-border/70 bg-muted/30 px-2 py-1">←</span>
                          <span className="inline-flex items-center gap-1 rounded border border-border/70 bg-muted/30 px-2 py-1">→</span>
                          <span>{uiText("team.text_e58887e68d")}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                        type="button"
                        className="rounded border border-border/70 bg-muted/30 px-2 py-1 text-[11px] hover:bg-muted/50"
                        onClick={async () => {
                          if (normalizedIndex === null) return;
                          const url = new URL(window.location.href);
                          url.searchParams.set("team", memberKey(members[normalizedIndex], `${normalizedIndex + 1}`));
                          try {
                            await navigator.clipboard.writeText(`${url.origin}${url.pathname}${url.search}${url.hash}`);
                            setCopied(true);
                            window.setTimeout(() => setCopied(false), 1500);
                          } catch {
                            setCopied(false);
                          }
                        }}>
                        
                            {copied ? uiText("team.text_e5b7b2e5a4") : uiText("team.text_e5a48de588")}
                          </button>
                          <div className="tabular-nums">{(normalizedIndex ?? 0) + 1} / {count}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div> :

            <div className="p-6">
                  <div className="text-sm text-muted-foreground">{uiText("team.text_e69caae689")}</div>
                </div>
            }
            </div>
          </div>
        }
      </div>
    </section>);

}

function TestimonialsPage() {
  const t = content.testimonials;
  useSeo(t?.browserTitle || t?.seoH1 || uiText("testimonials.cic_5"), t?.subtitle);
  return (
    <PageShell title={t?.seoH1 || t?.title || uiText("testimonials.text_e5aea2e688")} subtitle={t?.subtitle}>
      <TestimonialsSection />
      <ContentAttribution />
    </PageShell>);

}

function TestimonialsSection({ preview = false }) {
  const t = content.testimonials ?? {};
  const [visible, setVisible] = React.useState(false);
  const [statsVisible, setStatsVisible] = React.useState(false);
  const sectionRef = React.useRef(null);
  const statsRef = React.useRef(null);
  const isPage = !preview;
  const stats = (content.network.globalStats ?? []).slice(0, 4);
  const statValues = [
  useCountUp(stats[0]?.value ?? 0, 2200, statsVisible && isPage),
  useCountUp(stats[1]?.value ?? 0, 2200, statsVisible && isPage),
  useCountUp(stats[2]?.value ?? 0, 2200, statsVisible && isPage),
  useCountUp(stats[3]?.value ?? 0, 2200, statsVisible && isPage)];

  const items = (t.items ?? t.cases ?? []).map((item, index) => ({
    ...item,
    layout: isPage ? "detailed" : item.layout ?? "detailed",
    img: item.imageUrl?.trim() || [
    "/images/testimonials/zhang.jpg",
    "/images/testimonials/wang.jpg",
    "/images/testimonials/li.jpg",
    "/images/testimonials/chen.jpg",
    "/images/testimonials/malaysia.jpg",
    "/images/testimonials/australia.jpg"][
    index] || "/images/testimonials/zhang.jpg"
  }));
  const pageIntro = (t.pageIntro ?? "").trim();
  const verifiedLabel = (t.verifiedLabel ?? uiText("testimonials.text_e5b7b2e9aa")).trim();
  const caseStudies = t.caseStudies ?? [];

  React.useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setVisible(true);
    }, { threshold: 0.08 });
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    if (!isPage || !statsRef.current) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setStatsVisible(true);
    }, { threshold: 0.12 });
    observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, [isPage]);

  const contactCta = (className, label) => <button type="button" onClick={openCustomerService} className={className}>{label}</button>;

  const module =
  <section id="testimonials" ref={sectionRef} className="apple-section anli-light-section">
      <div className="max-w-7xl mx-auto">
        {!isPage &&
      <div className={`text-center mb-10 sm:mb-12 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <h2 className="anli-section-title">{t.title || uiText("testimonials.text_e5aea2e688")}</h2>
            <p className="anli-section-subtitle mt-2.5 sm:mt-3">{t.subtitle || uiText("testimonials.text_e69da5e887")}</p>
          </div>
      }

        {isPage && pageIntro ?
      <p className={`max-w-3xl mx-auto text-center text-[14px] sm:text-[15px] text-muted-foreground leading-relaxed mb-8 sm:mb-10 section-padding pt-6 sm:pt-8 ${visible ? "opacity-100" : "opacity-0"} transition-opacity duration-700`}>
            {pageIntro}
          </p> :
      null}

        {isPage && stats.length > 0 ?
      <div ref={statsRef} className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-10 sm:mb-12 max-w-5xl mx-auto">
            {stats.map((item, index) =>
        <div className="rounded-xl border border-neutral-200/90 bg-white px-3 py-4 sm:py-5 text-center shadow-sm" key={item.label}>
                <div className="font-sans text-xl sm:text-2xl font-bold text-neutral-900 tabular-nums">{statValues[index]}{item.suffix}</div>
                <div className="mt-1 text-[11px] sm:text-xs text-neutral-600">{item.label}</div>
              </div>
        )}
          </div> :
      null}

        <div className="grid gap-4 sm:gap-6 sm:grid-cols-2">
          {items.map((item, index) => {
          const compact = item.layout === "compact";
          return (
            <div
              className={`rounded-xl border border-neutral-200/90 bg-white p-4 sm:p-6 shadow-sm transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
              style={{ transitionDelay: `${150 + 80 * index}ms` }}
              key={`${item.name || item.title}-${index}`}>
              
                {compact ?
              <TestimonialCompactCard item={item} visible={visible} /> :

              <TestimonialDetailedCard item={item} visible={visible} verifiedLabel={verifiedLabel} isPage={isPage} />
              }
              </div>);

        })}
        </div>

        <div className={`mt-8 sm:mt-12 anli-inline-cta-panel p-5 sm:p-10 text-center transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0"}`} style={{ transitionDelay: "500ms" }}>
          <p className="font-sans text-[1.02rem] sm:text-lg font-semibold text-neutral-900">{t.ctaTitle}</p>
          <p className="mx-auto mt-1.5 sm:mt-2 max-w-xl text-[12.5px] sm:text-sm text-neutral-500">{t.ctaSubtitle}</p>
          <div className="mt-5 sm:mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
            {isPage ?
          <>
                <Link to="/advantages" className="anli-btn-neutral w-full sm:w-auto">{uiText("testimonials.text_e4ba86e8a7_3")}</Link>
                {contactCta("anli-btn-dark w-full sm:w-auto", t.ctaSecondary)}
              </> :

          <>
                <Link to="/testimonials" className="anli-btn-neutral w-full sm:w-auto">{t.ctaPrimary}</Link>
                {contactCta("anli-btn-dark w-full sm:w-auto", t.ctaSecondary)}
              </>
          }
          </div>
        </div>

        {isPage && caseStudies.length > 0 && (t.caseStudiesTitle ?? "").length > 0 ?
      <div className="mt-14 sm:mt-16 border-t border-neutral-200/80 pt-12 sm:pt-14">
            <div className="text-center mb-8 sm:mb-10">
              <h2 className="anli-section-title">{t.caseStudiesTitle}</h2>
              {t.caseStudiesSubtitle ? <p className="anli-section-subtitle mt-2.5">{t.caseStudiesSubtitle}</p> : null}
            </div>
            <div className="grid md:grid-cols-3 gap-4 sm:gap-6">
              {caseStudies.map((item) =>
          <div className="rounded-xl border border-neutral-200/90 bg-white p-5 sm:p-6 shadow-sm" key={item.title}>
                  <h3 className="font-bold text-neutral-900 text-[15px] sm:text-base leading-snug">{item.title}</h3>
                  <p className="mt-2.5 text-[12.5px] sm:text-[13.5px] text-muted-foreground leading-relaxed">{item.description}</p>
                  {item.tags?.length > 0 ?
            <div className="mt-4 flex flex-wrap gap-1.5">
                      {item.tags.map((tag) => <span className="inline-flex rounded-md bg-neutral-100 px-2 py-0.5 text-[10px] sm:text-[11px] font-medium text-neutral-700" key={tag}>{tag}</span>)}
                    </div> :
            null}
                </div>
          )}
            </div>
          </div> :
      null}

        {isPage && (t.pageBottomTitle ?? "").trim() ?
      <div className="section-padding pb-12 sm:pb-16 pt-4">
            <div className="anli-dark-band max-w-4xl mx-auto rounded-2xl px-6 sm:px-12 py-10 sm:py-12 text-center shadow-lg">
              <h2 className="text-xl sm:text-2xl font-bold tracking-tight">{t.pageBottomTitle}</h2>
              {t.pageBottomSubtitle ? <p className="mt-3 text-[13px] sm:text-[15px] text-gray-400 max-w-2xl mx-auto leading-relaxed">{t.pageBottomSubtitle}</p> : null}
              <div className="mt-6 flex justify-center">
                <button type="button" className="anli-btn-on-dark px-8 py-3" onClick={openCustomerService}>{(t.pageBottomCtaText ?? t.ctaSecondary).trim()}</button>
              </div>
            </div>
          </div> :
      null}
      </div>
    </section>;


  return preview ?
  <div className="transition-all duration-700 ease-out motion-reduce:transition-none opacity-100 translate-y-0 motion-reduce:opacity-100 motion-reduce:translate-y-0" style={{ transitionDelay: "200ms" }}>
      {module}
    </div> :
  module;
}

function CountedAmount({ target, visible, className, display }) {
  if (display) return <span className={className}>{display}</span>;
  const amount = useCountUp(Number(target || 0), 2500, visible);
  return <span className={className}>${amount.toLocaleString("en-US")}</span>;
}

function TestimonialCompactCard({ item, visible }) {
  return (
    <div className="flex flex-col gap-3.5 sm:gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 items-center gap-3">
        <img src={item.img} alt="" className="h-12 w-12 shrink-0 rounded-full object-cover" loading="lazy" />
        <div className="min-w-0">
          <div className="font-sans text-[15px] sm:text-base font-semibold text-neutral-900">{item.name || item.title}</div>
          <div className="text-[13px] sm:text-sm text-neutral-500">{item.location || item.role}</div>
          <div className="mt-1 flex flex-wrap items-center gap-x-1 text-[11px] sm:text-xs text-neutral-500">
            <Clock3 className="mr-0.5 inline h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>{item.timeline}</span>
            <span className="text-neutral-300">·</span>
            <span>{item.date}</span>
          </div>
        </div>
      </div>
      <div className="shrink-0 border-t border-neutral-100 pt-3.5 sm:pt-4 text-left sm:border-0 sm:pt-0 sm:text-right">
        <div className="text-[11px] sm:text-xs font-medium text-neutral-500">{uiText("testimonials.text_e8bfbde59b")}</div>
        <div className="anli-recovery-amount mt-0.5 text-2xl sm:text-3xl">
          <CountedAmount target={item.amount} visible={visible} display={item.amountText} />
        </div>
      </div>
    </div>);

}

function TestimonialDetailedCard({ item, visible, verifiedLabel, isPage }) {
  return (
    <>
      <div className="mb-3.5 sm:mb-4 flex flex-wrap items-center justify-between gap-2">
        <span className="inline-flex items-center rounded-full bg-neutral-100 px-2.5 sm:px-3 py-1 text-[11px] sm:text-xs font-medium text-neutral-700">{item.type || item.scamType}</span>
        <span className="flex items-center gap-1 text-[11px] font-medium text-neutral-500">
          <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(152_76%_32%)]" aria-hidden="true" />
          {verifiedLabel}
        </span>
      </div>
      {isPage ? <p className="mb-2 font-serif text-3xl leading-none text-neutral-200 select-none" aria-hidden="true">“</p> : null}
      <blockquote className="mb-4.5 sm:mb-6 font-sans text-[12.5px] sm:text-sm leading-relaxed text-neutral-600">
        {isPage ? <span className="italic">{item.quote || item.desc || item.content || item.feedback}</span> : item.quote || item.desc || item.content || item.feedback}
      </blockquote>
      <div className="flex flex-col gap-3.5 sm:gap-4 border-t border-neutral-100 pt-3.5 sm:pt-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <img src={item.img} alt="" className="h-10 w-10 shrink-0 rounded-full object-cover" loading="lazy" />
          <div>
            <div className="text-[13px] sm:text-sm font-semibold text-neutral-900">{item.name || item.title}</div>
            <div className="text-[11px] sm:text-xs text-neutral-500">{item.location || item.role}</div>
            <div className="mt-0.5 flex items-center gap-1 text-[10px] sm:text-[11px] text-neutral-500">
              <Clock3 className="h-3 w-3" aria-hidden="true" />
              {item.timeline}
              <span className="text-neutral-300">·</span>
              {item.date}
            </div>
          </div>
        </div>
        <div className="text-left sm:text-right">
          <div className="text-[11px] sm:text-xs font-medium text-neutral-500">{uiText("testimonials.text_e8bfbde59b")}</div>
          <div className="anli-recovery-amount mt-0.5 text-2xl sm:text-3xl">
            <CountedAmount target={item.amount} visible={visible} display={item.amountText} />
          </div>
        </div>
      </div>
    </>);

}

function ScamTypesPage() {
  const scam = content.scamTypes;
  useSeo(scam.seoH1, scam.pageSubtitle);
  return (
    <PageShell eyebrow={scam.pageEyebrow} title={scam.seoH1 || scam.pageTitle} subtitle={scam.pageSubtitle}>
      <ScamTypesSection />
      <ContentAttribution />
    </PageShell>);

}

const scamRiskClasses = {
  [uiText("scamTypes.risk_level_high")]: "bg-red-600 text-white shadow-sm",
  [uiText("scamTypes.text_e4b8ade9a3")]: "bg-orange-500 text-white shadow-sm",
  [uiText("scamTypes.risk_level_low")]: "bg-[hsl(168_48%_38%)] text-white shadow-sm"
};

function ScamTypesSection({ preview = false }) {
  const scam = content.scamTypes;
  const [visible, setVisible] = React.useState(false);
  const sectionRef = React.useRef(null);
  const isPage = !preview;
  const warningSignals = scam.warningSignals ?? [];
  const protectionBlocks = scam.protectionBlocks ?? [];

  React.useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setVisible(true);
    }, { threshold: 0.05 });

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const items = scam.items.map((item) => ({
    ...item,
    img: item.imageUrl?.trim() || "/images/scams/crypto.jpg"
  }));

  const contactButton = (className, label) =>
  <button type="button" onClick={openCustomerService} className={className}>
      {label}
    </button>;


  const listTitle = (scam.listSectionTitle ?? scam.title).trim();
  const listSubtitle = (scam.listSectionSubtitle ?? scam.subtitle).trim();

  return (
    <section id="scam-types" ref={sectionRef} className="apple-section anli-light-section">
      <div className="max-w-7xl mx-auto">
        {preview ?
        <div className={`text-center mb-10 sm:mb-12 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <h2 className="anli-section-title">{scam.title}</h2>
            <p className="anli-section-subtitle mt-2.5 sm:mt-3">{scam.subtitle}</p>
          </div> :

        <div className={`text-center mb-8 sm:mb-10 section-padding pt-6 sm:pt-8 ${visible ? "opacity-100" : "opacity-0"} transition-opacity duration-700`}>
            <h2 className="anli-section-title">{listTitle}</h2>
            <p className="anli-section-subtitle mt-2.5 sm:mt-3">{listSubtitle}</p>
          </div>
        }

        <div className="grid gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((item, index) => {
            const riskClass = scamRiskClasses[item.risk] ?? scamRiskClasses[uiText("scamTypes.text_e4b8ade9a3")];
            const slug = slugOf(item.title);
            return (
              <article
                className={`overflow-hidden rounded-xl border border-neutral-200/90 bg-white shadow-sm transition-all duration-700 flex flex-col ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
                style={{ transitionDelay: `${200 + index * 100}ms` }}
                key={item.title}>
                
                <Link to={`/scam-types/${slug}`} className="relative block aspect-[16/9] overflow-hidden bg-neutral-900 group">
                  <img src={item.img} alt={item.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" loading="lazy" />
                  <div className="absolute left-3 top-3">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${riskClass}`}>
                      <TriangleAlert className="h-3 w-3 opacity-90" aria-hidden="true" />
                      {item.risk}
                    </span>
                  </div>
                </Link>

                <div className="flex items-center justify-between gap-2.5 sm:gap-3 border-b border-neutral-200 px-3.5 sm:px-4 py-2.5 sm:py-3 text-[13px] sm:text-sm">
                  <div className="flex min-w-0 items-center gap-2 text-neutral-800">
                    <TrendingUp className="h-4 w-4 shrink-0 text-neutral-500" aria-hidden="true" />
                    <span className="truncate font-medium">{uiText("scamTypes.text_e5b7b2e5a4_2")}{item.cases}</span>
                  </div>
                  <div className="h-4 w-px shrink-0 bg-neutral-200" aria-hidden="true" />
                  <div className="shrink-0 text-neutral-800">{uiText("scamTypes.text_e58a9ee6a1")}
                    <span className="anli-scam-rate">{item.rate}</span>
                  </div>
                </div>

                <div className="p-4 sm:p-6 flex flex-col flex-1">
                  <h3 className="font-sans text-base sm:text-lg font-bold text-neutral-900 sm:text-xl">
                    <Link to={`/scam-types/${slug}`} className="hover:text-amber-800 transition-colors">
                      {item.title}
                    </Link>
                  </h3>
                  <div className="mt-1.5 sm:mt-2 flex items-start gap-2">
                    <DollarSign className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" strokeWidth={2.25} aria-hidden="true" />
                    <div className="text-[13px] sm:text-sm leading-snug">
                      <span className="text-neutral-500">{uiText("scamTypes.text_e6b689e6a1")}</span>
                      <span className="font-semibold text-neutral-900">{item.range}</span>
                    </div>
                  </div>
                  <p className="mt-2.5 sm:mt-3 font-sans text-[12.5px] sm:text-sm leading-relaxed text-neutral-600 flex-1">{item.desc}</p>
                  <div className="mt-3.5 sm:mt-4 text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide text-neutral-500">{uiText("scamTypes.text_e5b8b8e8a7")}</div>
                  <div className="mt-1.5 sm:mt-2 flex flex-wrap gap-1.5 sm:gap-2">
                    {item.tags.map((tag, tagIndex) =>
                    <span className={`rounded-full px-2.5 py-1 text-[11px] sm:text-xs font-medium ${tagIndex % 2 === 0 ? "border border-rose-100 bg-rose-50 text-rose-900" : "border border-orange-100 bg-orange-50 text-orange-950"}`} key={tag}>
                        {tag}
                      </span>
                    )}
                  </div>
                  <div className="mt-3.5 sm:mt-4 flex flex-wrap items-center justify-center gap-x-4 sm:gap-x-5 gap-y-1 border-t border-neutral-100 pt-3.5 sm:pt-4 text-[10px] sm:text-[11px] text-neutral-500">
                    <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-red-500" />{uiText("scamTypes.text_e9ab98e58d")}</span>
                    <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-orange-500" />{uiText("scamTypes.text_e4b8ade58d")}</span>
                    <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[hsl(168_55%_38%)]" />{uiText("scamTypes.text_e4bd8ee58d")}</span>
                  </div>
                  {isPage ?
                  <Link to={`/scam-types/${slug}`} className="mt-4 inline-flex items-center justify-center gap-1 text-[13px] font-semibold text-amber-800 hover:text-amber-950">{uiText("scamTypes.text_e69fa5e79c_4")}
                    <ArrowRight className="w-4 h-4" />
                    </Link> :
                  null}
                </div>
              </article>);

          })}
        </div>

        <div className={`anli-inline-cta-panel mx-auto mt-8 sm:mt-12 max-w-4xl px-5 sm:px-10 py-6 sm:py-12 text-center transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0"}`} style={{ transitionDelay: "500ms" }}>
          <div className="mx-auto mb-4 sm:mb-5 flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-full bg-neutral-100 shadow-sm">
            <TriangleAlert className="h-7 w-7 text-neutral-700" strokeWidth={2} aria-hidden="true" />
          </div>
          <p className="font-sans text-[1.02rem] sm:text-lg font-bold text-neutral-900 sm:text-xl">{scam.ctaTitle}</p>
          <p className="mx-auto mt-1.5 sm:mt-2 max-w-lg text-[12.5px] sm:text-sm leading-relaxed text-neutral-600 sm:text-[15px]">{scam.ctaSubtitle}</p>
          <div className="mt-5 sm:mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
            {isPage ?
            <>
                <Link to="/process" className="anli-btn-neutral w-full min-w-[200px] sm:w-auto">{uiText("process.text_e4ba86e8a7")}</Link>
                {contactButton("anli-btn-dark w-full min-w-[200px] sm:w-auto", scam.ctaSecondary)}
              </> :

            <>
                <Link to="/scam-types" className="anli-btn-neutral w-full min-w-[200px] sm:w-auto">{scam.ctaPrimary}</Link>
                {contactButton("anli-btn-dark w-full min-w-[200px] sm:w-auto", scam.ctaSecondary)}
              </>
            }
          </div>
        </div>

        {isPage && warningSignals.length > 0 && scam.warningSectionTitle ?
        <div className="mt-14 sm:mt-16 border-t border-neutral-200/80 pt-12 sm:pt-14 bg-neutral-50/90 -mx-4 px-4 sm:mx-0 sm:px-0 sm:bg-transparent">
            <div className="max-w-7xl mx-auto">
              {scam.warningSectionEyebrow ? <p className="text-center text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-800/90 mb-2">{scam.warningSectionEyebrow}</p> : null}
              <div className="text-center mb-8">
                <h2 className="anli-section-title">{scam.warningSectionTitle}</h2>
                {scam.warningSectionSubtitle ? <p className="anli-section-subtitle mt-2.5 max-w-2xl mx-auto">{scam.warningSectionSubtitle}</p> : null}
              </div>
              <ul className="grid sm:grid-cols-2 gap-3 sm:gap-4 max-w-4xl mx-auto">
                {warningSignals.map((signal) =>
              <li className="flex gap-3 rounded-xl border border-neutral-200/90 bg-white px-4 py-3 text-[13px] text-neutral-800 shadow-sm" key={signal}>
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-500/10 text-red-700 font-bold text-xs" aria-hidden="true">!</span>
                    <span className="leading-relaxed">{signal}</span>
                  </li>
              )}
              </ul>
            </div>
          </div> :
        null}

        {isPage && protectionBlocks.length > 0 && scam.protectionSectionTitle ?
        <div className="mt-12 sm:mt-16 section-padding pb-4">
            {scam.protectionSectionEyebrow ? <p className="text-center text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-800/90 mb-2">{scam.protectionSectionEyebrow}</p> : null}
            <div className="text-center mb-8 sm:mb-10">
              <h2 className="anli-section-title flex items-center justify-center gap-2">
                <Shield className="h-7 w-7 text-neutral-700 shrink-0" aria-hidden="true" />
                {scam.protectionSectionTitle}
              </h2>
              {scam.protectionSectionSubtitle ? <p className="anli-section-subtitle mt-2.5 max-w-2xl mx-auto">{scam.protectionSectionSubtitle}</p> : null}
            </div>
            <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
              {protectionBlocks.map((block) =>
            <div className="rounded-xl border border-neutral-200/90 bg-white p-5 sm:p-6 shadow-sm" key={block.title}>
                  <h3 className="font-bold text-neutral-900 text-[15px] sm:text-lg">{block.title}</h3>
                  <p className="mt-2 text-[12.5px] sm:text-[13.5px] text-muted-foreground leading-relaxed">{block.intro}</p>
                  <ul className="mt-4 space-y-2">
                    {block.bullets.map((bullet) =>
                <li className="flex gap-2 text-[12px] sm:text-[13px] text-neutral-700 leading-relaxed" key={bullet}>
                        <span className="text-emerald-600 font-bold shrink-0" aria-hidden="true">✓</span>
                        <span>{bullet}</span>
                      </li>
                )}
                  </ul>
                </div>
            )}
            </div>
          </div> :
        null}

        {isPage && scam.emergencyBannerTitle?.trim() ?
        <div className="section-padding pb-12 sm:pb-16">
            <div className="anli-dark-band max-w-4xl mx-auto rounded-2xl px-6 sm:px-12 py-10 sm:py-12 text-center shadow-lg">
              <h2 className="text-xl sm:text-2xl font-bold tracking-tight">{scam.emergencyBannerTitle}</h2>
              {scam.emergencyBannerSubtitle ? <p className="mt-3 text-[13px] sm:text-[15px] text-gray-400 max-w-2xl mx-auto leading-relaxed">{scam.emergencyBannerSubtitle}</p> : null}
              <div className="mt-6 flex justify-center">
                <button type="button" onClick={openCustomerService} className="anli-btn-on-dark px-8 py-3">
                  {(scam.emergencyBannerCtaText ?? scam.ctaSecondary).trim()}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div> :
        null}
      </div>
    </section>);

}

function ScamTypeDetail() {
  const { slug } = useParams();
  const item = content.scamTypes.items.find((entry) => matchesSlug(entry.title, slug));
  useSeo(item?.seoH1 || uiText("scamTypes.cic_6"), item?.desc || uiText("scamTypes.text_e69caae689_2"));
  if (!item) {
    return (
      <PageShell title={uiText("scamTypes.cic_6")} subtitle={content.news?.notFoundText || uiText("scamTypes.text_e69caae689_2")}>
        <section className="section center" />
      </PageShell>);

  }

  const detailTitle = item.seoH1?.trim() ?
  item.seoH1.trim() :
  `${item.title}${content.scamTypes.detailPageTitleSuffix ?? ""}`;
  const imageUrl = item.imageUrl?.trim();
  const relatedRegions = (content.network?.regions ?? []).slice(0, 4);
  const contactEmailText = content.contact?.email?.trim() || uiText("scamTypes.text_e88194e7b3_3");
  const detailStatsLabels = {
    range: uiText("scamTypes.text_e6b689e6a1_2"),
    cases: uiText("scamTypes.text_e5b7b2e5a4_3"),
    rate: uiText("scamTypes.text_e58a9ee6a1")
  };
  const detailStatsNote = uiText("scamTypes.text_e8afb4e698");

  return (
    <Layout>
      <div className="pt-14 lg:pt-16">
        <div className="subpage-content">
          <div className="section-padding py-12 lg:py-16">
            <article className="max-w-4xl mx-auto space-y-6">
              <h1 className="font-sans text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl">
                {detailTitle}
              </h1>
              {imageUrl ?
              <div className="overflow-hidden rounded-lg border border-border">
                  <img src={imageUrl} alt="" className="max-h-72 w-full object-cover" loading="lazy" />
                </div> :
              null}
              <p className="text-neutral-600 leading-[1.65]">{item.desc}</p>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="rounded-xl border border-neutral-200/90 bg-white p-4 shadow-sm">
                  <p className="text-sm text-neutral-500">{detailStatsLabels.range}</p>
                  <p className="font-sans font-semibold text-neutral-900">{item.range}</p>
                </div>
                <div className="rounded-xl border border-neutral-200/90 bg-white p-4 shadow-sm">
                  <p className="text-sm text-neutral-500">{detailStatsLabels.cases}</p>
                  <p className="font-sans font-semibold text-neutral-900">{item.cases}</p>
                </div>
                <div className="rounded-xl border border-neutral-200/90 bg-white p-4 shadow-sm">
                  <p className="text-sm text-neutral-500">{detailStatsLabels.rate}</p>
                  <p className="font-sans font-semibold text-neutral-900">{item.rate}</p>
                </div>
              </div>
              <p className="text-[12px] sm:text-sm text-neutral-500 leading-relaxed">{detailStatsNote}</p>
              <div>
                <h2 className="font-sans text-2xl font-bold text-neutral-900 mb-2">
                  {content.scamTypes.detailFeaturesTitle ?? uiText("scamTypes.text_e5b8b8e8a7_2")}
                </h2>
                <ul className="flex flex-wrap gap-2">
                  {item.tags.map((tag) =>
                  <li className="px-2 py-1 rounded-md bg-amber-500/10 text-sm text-neutral-800" key={tag}>
                      {tag}
                    </li>
                  )}
                </ul>
              </div>
              <p className="text-neutral-600 leading-[1.65]">
                {(content.scamTypes.detailContactTemplate || "").replace("{email}", contactEmailText)}
              </p>
              <section className="space-y-2">
                <h2 className="font-sans text-2xl font-bold text-neutral-900">
                  {content.scamTypes.detailRelatedRegionsTitle ?? uiText("scamTypes.text_e79bb8e585")}
                </h2>
                <div className="flex flex-wrap gap-2">
                  {relatedRegions.map((region) =>
                  <Link
                    to={`/regions/${slugOf(region.name)}`}
                    className="px-2 py-1 rounded-md border border-neutral-200 bg-white text-sm text-neutral-700 hover:bg-amber-50/60"
                    key={region.name}>
                    
                      {region.name}
                    </Link>
                  )}
                </div>
              </section>
              <Link to="/" className="inline-flex font-medium text-amber-700 underline underline-offset-4 hover:text-amber-800">
                {content.scamTypes.detailBackHomeText ?? uiText("scamTypes.text_e8bf94e59b")}
              </Link>
            </article>
          </div>
          <ContentAttribution />
        </div>
      </div>
    </Layout>);


  /*
  return (
    <PageShell title={item.seoH1 || `${item.title}${content.scamTypes.detailPageTitleSuffix}`} subtitle={item.desc}>
      <section className="section detail-layout">
        <article className="detail-main">
          <img className="detail-image" src={item.imageUrl} alt="" />
          <div className="stats-grid compact">
            <Stat value={item.range} label={uiText("scamTypes.text_e6b689e6a1_2")} />
            <Stat value={item.cases} label={uiText("scamTypes.text_e5b7b2e5a4_3")} />
            <Stat value={item.risk} label={uiText("scamTypes.risk_level_label")} />
          </div>
          <h2>{content.scamTypes.detailFeaturesTitle}</h2>
          <div className="tag-row">{item.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
          <p>{content.scamTypes.detailContactTemplate.replace("{email}", content.contact.email)}</p>
        </article>
      </section>
      <ContentAttribution />
    </PageShell>
  );
  */
}

function NetworkPage() {
  const network = content.network;
  useSeo(network?.seoH1 || uiText("network.cic_7"), network?.subtitle || network?.networkPageTagline);
  return (
    <PageShell title={network?.seoH1 || uiText("layout.text_e585a8e790")} subtitle={network?.subtitle || network?.networkPageTagline}>
      <NetworkSection />
      <ContentAttribution />
    </PageShell>);

}

const networkMapPaths = [
{ key: "na", d: "M 768 188 Q 480 55 218 170" },
{ key: "eu", d: "M 768 188 Q 515 88 390 146" },
{ key: "ap1", d: "M 768 188 Q 655 228 682 250" },
{ key: "ap2", d: "M 768 188 Q 805 235 865 276" },
{ key: "gc", d: "M 768 188 Q 752 202 738 218" },
{ key: "me", d: "M 768 188 Q 625 158 545 170" }];


const networkMapNodes = [
{ cx: 768, cy: 188, r: 15, label: uiText("network.text_e696b0e58a"), slug: "apac", kind: "hub" },
{ cx: 738, cy: 218, r: 6.5, label: uiText("network.text_e4b88ae6b5"), slug: "greater-china", kind: "center" },
{ cx: 218, cy: 170, r: 8, label: uiText("network.text_e7b490e7b4"), slug: "americas", kind: "center" },
{ cx: 390, cy: 146, r: 7, label: uiText("network.text_e580abe695"), slug: "europe", kind: "center" },
{ cx: 428, cy: 126, r: 4.5, label: uiText("network.text_e6b395e898"), slug: "europe", kind: "branch" },
{ cx: 682, cy: 250, r: 9, label: uiText("network.text_e9a699e6b8"), slug: "apac", kind: "center" },
{ cx: 865, cy: 276, r: 8, label: uiText("network.text_e68289e5b0"), slug: "apac", kind: "branch" },
{ cx: 810, cy: 156, r: 8.5, label: uiText("network.text_e69db1e4ba"), slug: "apac", kind: "center" },
{ cx: 545, cy: 170, r: 5, label: uiText("network.text_e8bfaae68b"), slug: "africa-middle-east", kind: "branch" },
{ cx: 298, cy: 208, r: 4, label: uiText("network.text_e5a49ae580"), slug: "americas", kind: "partner" }];


function NetworkSection({ preview = false }) {
  const network = content.network;
  const regions = network.regions ?? [];
  const [visible, setVisible] = React.useState(false);
  const [activeRegionIndex, setActiveRegionIndex] = React.useState(0);
  const [activeCity, setActiveCity] = React.useState(null);
  const [copied, setCopied] = React.useState(false);
  const sectionRef = React.useRef(null);
  const officeRefs = React.useRef([]);
  const offices = (network.offices ?? []).slice(0, preview ? 3 : undefined);
  const regionHighlights = network.regionHighlights?.length ? network.regionHighlights : regions;
  const representativeCities = Array.from(new Set(regions.flatMap((region) => region.cities ?? [])));
  const activeRegion = regions[activeRegionIndex] || regions[0];
  const activeRegionSlug = activeRegion?.slug || "apac";
  const cityLabelForOffice = (office, regionIndex) => {
    const regionCities = regions[regionIndex]?.cities ?? [];
    if (regionCities.includes(office.city)) return office.city;
    return regionCities.find((city) => city === uiText("network.text_e69d9ce68b") && office.city === uiText("network.text_e8bfaae68b_2")) || office.city;
  };

  const regionIndexFromSlug = React.useCallback((value) => {
    const target = String(value || "").trim();
    if (!target || !regions.length) return -1;
    return regions.findIndex((region) =>
    region.slug === target ||
    region.name === target ||
    slugOf(region.name) === target ||
    decodeURIComponent(target) === region.name
    );
  }, [regions]);

  const syncFromUrl = React.useCallback(() => {
    if (!regions.length) return;
    const params = new URL(window.location.href).searchParams;
    const city = params.get("city")?.trim();
    const regionParam = params.get("region")?.trim();
    let nextRegion = regionIndexFromSlug(regionParam);
    let nextCity = null;

    if (city) {
      const cityRegion = regions.findIndex((region) => (region.cities ?? []).includes(city));
      if (cityRegion >= 0) {
        nextRegion = cityRegion;
        nextCity = city;
      }
    }

    setActiveRegionIndex(nextRegion >= 0 ? nextRegion : 0);
    setActiveCity(nextCity);
  }, [regionIndexFromSlug, regions]);

  React.useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setVisible(true);
    }, { threshold: 0.06 });
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    syncFromUrl();
  }, [syncFromUrl]);

  React.useEffect(() => {
    const regionParam = new URL(window.location.href).searchParams.get("region");
    if (!regionParam?.trim()) return;
    const frame = requestAnimationFrame(() => {
      sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  React.useEffect(() => {
    const onPopState = () => syncFromUrl();
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [syncFromUrl]);

  React.useEffect(() => {
    if (activeRegionIndex >= regions.length) setActiveRegionIndex(0);
  }, [activeRegionIndex, regions.length]);

  React.useEffect(() => {
    if (!activeCity || !offices.length) return;
    const officeIndex = offices.findIndex((office) => office.city === activeCity);
    if (officeIndex < 0) return;
    const timer = window.setTimeout(() => {
      officeRefs.current[officeIndex]?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 150);
    return () => window.clearTimeout(timer);
  }, [activeCity, offices]);

  const writeViewUrl = (regionIndex, city) => {
    if (!regions.length) return;
    const normalizedIndex = (regionIndex % regions.length + regions.length) % regions.length;
    const url = new URL(window.location.href);
    url.searchParams.set("region", regions[normalizedIndex].slug || slugOf(regions[normalizedIndex].name));
    if (city?.trim()) {
      url.searchParams.set("city", city.trim());
    } else {
      url.searchParams.delete("city");
    }
    window.history.replaceState({}, "", `${url.pathname}?${url.searchParams.toString()}${url.hash}`);
  };

  const selectRegion = (regionIndex, city = null) => {
    if (!regions.length) return;
    const normalizedIndex = (regionIndex % regions.length + regions.length) % regions.length;
    setActiveRegionIndex(normalizedIndex);
    setActiveCity(city?.trim() || null);
    writeViewUrl(normalizedIndex, city);
  };

  const selectCity = (city, regionIndex) => {
    selectRegion(regionIndex, city);
    const officeIndex = offices.findIndex((office) => office.city === city);
    if (officeIndex >= 0) {
      requestAnimationFrame(() => {
        officeRefs.current[officeIndex]?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
    }
  };

  const copyCurrentView = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (preview) {
    const citySet = new Set(activeRegion?.cities ?? []);

    return (
      <Section id="network" className="anli-light-section">
        <div className="network-visual">
          <div className="network-visual-head">
            <div className="network-visual-title">
              <h2>{network.title || network.officesHeading}</h2>
              <p>{network.subtitle || network.officesSubheading}</p>
            </div>
            <div className="network-visual-stats">
              {(network.globalStats ?? []).map((stat) =>
              <div className="network-stat" key={stat.label}>
                  <strong>{stat.value}{stat.suffix || ""}</strong>
                  <span>{stat.label}</span>
                </div>
              )}
            </div>
          </div>

          <NetworkMapSvg activeRegionSlug={activeRegionSlug} legendItems={network.legendItems ?? []} onSelectRegionSlug={(slug) => {
            const index = regionIndexFromSlug(slug);
            if (index >= 0) setActiveRegionIndex(index);
          }} />

          <div className="network-highlight-row">
            <div className="network-highlight-grid">
              {regionHighlights.slice(0, 4).map((region, index) =>
              <button type="button" key={region.slug || region.name} className={region.slug === activeRegionSlug ? "is-active" : ""} onClick={() => setActiveRegionIndex(index)}>
                  <strong>{region.count}</strong>
                  <span>{region.label || region.name}</span>
                </button>
              )}
            </div>
            <Link className="network-map-cta" to="/global-network">{network.cta || uiText("network.text_e79c8be5ae")}</Link>
          </div>

          <div className="network-region-panel">
            <div className="network-region-head">
              <div>
                <h3>{uiText("network.text_e5a4a7e58d")}</h3>
                <p>{uiText("network.text_e9bb9ee693")}</p>
              </div>
              <span>{uiText("network.text_e9bb9ee693_2")}</span>
            </div>
            <div className="network-region-tabs">
              {regions.map((region, index) =>
              <button type="button" key={region.slug || region.name} className={region.slug === activeRegionSlug ? "is-active" : ""} onClick={() => setActiveRegionIndex(index)}>
                  {region.name}
                  <span> · {region.count}</span>
                </button>
              )}
            </div>
            {activeRegion &&
            <>
                <p className="network-region-summary">
                  <strong>{activeRegion.name}</strong>
                  <span>|</span>
                  <em>{activeRegion.count}</em>{uiText("network.text_e5808be58d")}
                <span>|</span>
                  {activeRegion.cities.length}{uiText("network.text_e5808be4bb")}
              </p>
                <div className="network-city-list">
                  <p>{uiText("network.text_e59f8ee5b8")}</p>
                  <div>
                    {representativeCities.map((city) =>
                  <button type="button" className={citySet.has(city) ? "is-active" : ""} key={city}>
                        <span />
                        {city}
                      </button>
                  )}
                  </div>
                </div>
              </>
            }
          </div>
        </div>
      </Section>);

  }

  return (
    <section ref={sectionRef} aria-label={uiText("network.text_e585a8e790_2")} className="bg-transparent pb-20 pt-10 lg:pt-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <NetworkPageMapVisual
          network={network}
          visible={visible}
          activeRegionSlug={activeRegionSlug}
          regionHighlights={regionHighlights}
          onSelectRegionSlug={(slug) => {
            const index = regionIndexFromSlug(slug);
            if (index >= 0) selectRegion(index);
          }} />
        

        <nav aria-label={uiText("network.text_e69cace9a1_3")} className="sticky top-14 z-30 mb-6 border-b border-neutral-200/90 bg-white/90 py-2.5 shadow-sm backdrop-blur-md lg:top-16">
          <div className="flex flex-wrap items-center justify-center gap-x-1 gap-y-2 px-2 text-[12px] font-semibold text-neutral-700 sm:gap-x-2 sm:text-[13px]">
            {[
            ["#network-cities", uiText("network.text_e59f8ee5b8_2")],
            ["#network-offices", uiText("network.text_e4b8bbe8a6")],
            ["#network-advantages", uiText("network.text_e7bd91e7bb")],
            ["#network-partners", uiText("network.text_e59088e4bd")],
            ["#network-cta", uiText("network.text_e592a8e8af")]].
            map(([href, label], index, items) =>
            <React.Fragment key={href}>
                <a href={href} className="rounded-full px-3 py-1.5 transition-colors hover:bg-amber-50 hover:text-amber-950">{label}</a>
                {index < items.length - 1 && <span className="text-neutral-300" aria-hidden="true">|</span>}
              </React.Fragment>
            )}
          </div>
        </nav>

        <div id="network-cities" className={cx("scroll-mt-28 mb-6 flex flex-wrap justify-center gap-2 transition-all duration-700 sm:gap-2.5", visible ? "opacity-100" : "opacity-0")}>
          {representativeCities.map((city) => {
            const regionIndex = regions.findIndex((region) => (region.cities ?? []).includes(city));
            const regionName = regions[regionIndex]?.name || "";
            const isActive = activeCity === city;
            const sameRegion = activeRegionIndex === regionIndex;
            return (
              <button
                type="button"
                key={`${regionName}-${city}`}
                onClick={() => selectCity(city, regionIndex)}
                className={cx(
                  "min-w-[5.5rem] rounded-lg border px-3 py-2 text-center transition-all sm:min-w-[6rem]",
                  isActive ?
                  "border-amber-500/70 bg-amber-50 text-neutral-900 shadow-sm ring-2 ring-amber-400/35" :
                  activeCity || !sameRegion ?
                  "border-neutral-200/90 bg-white text-neutral-700 hover:border-amber-300/50" :
                  "border-amber-300/55 bg-amber-50/50 text-neutral-900"
                )}>
                
                <span className="block text-sm font-semibold">{city}</span>
                <span className="mt-0.5 block text-[11px] text-neutral-500">{regionName}</span>
              </button>);

          })}
        </div>

        <div className="mb-10 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-6">
          {network.networkPageTagline && <p className="text-center font-sans text-sm font-medium text-neutral-700 sm:text-base">{network.networkPageTagline}</p>}
          <button type="button" onClick={copyCurrentView} className="inline-flex shrink-0 items-center gap-2 rounded-full border border-neutral-200 bg-white px-4 py-2 text-xs font-semibold text-neutral-700 shadow-sm transition-colors hover:bg-neutral-50">
            {copied ? <ClipboardCheck className="h-3.5 w-3.5 text-emerald-600" aria-hidden="true" /> : <Copy className="h-3.5 w-3.5" aria-hidden="true" />}
            {copied ? uiText("team.text_e5b7b2e5a4") : uiText("network.text_e5a48de588_2")}
          </button>
        </div>

        <div id="network-offices" className="mb-4 scroll-mt-32">
          <h2 className="font-sans text-2xl font-bold text-neutral-900 sm:text-3xl">{network.officesHeading}</h2>
          {network.officesSubheading && <p className="mt-2 text-sm text-neutral-600 sm:text-base">{network.officesSubheading}</p>}
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {offices.map((office, index) => {
            const regionIndex = regionIndexFromSlug(office.regionSlug);
            const isActive = activeCity ? activeCity === office.city : regionIndex === activeRegionIndex;
            return (
              <article
                ref={(node) => {officeRefs.current[index] = node;}}
                role="button"
                tabIndex={0}
                key={`${office.city}-${index}`}
                onClick={() => regionIndex >= 0 && selectCity(cityLabelForOffice(office, regionIndex), regionIndex)}
                onKeyDown={(event) => {
                  if ((event.key === "Enter" || event.key === " ") && regionIndex >= 0) {
                    event.preventDefault();
                    selectCity(cityLabelForOffice(office, regionIndex), regionIndex);
                  }
                }}
                className={cx(
                  "overflow-hidden rounded-xl border border-neutral-200/90 bg-white text-left shadow-md outline-none transition-all hover:shadow-lg",
                  isActive ? "border-amber-400/70 ring-2 ring-amber-400/70 ring-offset-2 ring-offset-[hsl(220_14%_97%)]" : ""
                )}>
                
                <div className="aspect-[16/10] overflow-hidden bg-neutral-200">
                  <img src={office.imageUrl?.trim() || "https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?auto=format&fit=crop&w=900&q=80"} alt={office.city} className="h-full w-full object-cover" loading="lazy" />
                </div>
                <div className="p-4 sm:p-5">
                  <div className="mb-3 flex items-start justify-between gap-2">
                    <div>
                      <h3 className="font-sans text-xl font-bold text-neutral-900">{office.city}</h3>
                      <p className="mt-0.5 text-sm text-neutral-500">{office.country}</p>
                    </div>
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-neutral-100">
                      <MapPin className="h-4 w-4 text-neutral-400" aria-hidden="true" />
                    </div>
                  </div>
                  <ul className="space-y-2.5 text-sm text-neutral-600">
                    <li className="flex gap-2.5">
                      <Building2 className="mt-0.5 h-4 w-4 shrink-0 text-neutral-400" aria-hidden="true" />
                      <span>{office.address}</span>
                    </li>
                    <li className="flex gap-2.5">
                      <Clock3 className="mt-0.5 h-4 w-4 shrink-0 text-neutral-400" aria-hidden="true" />
                      <span>{office.hours}</span>
                    </li>
                    <li className="flex gap-2.5">
                      <Languages className="mt-0.5 h-4 w-4 shrink-0 text-neutral-400" aria-hidden="true" />
                      <span>{office.languages}</span>
                    </li>
                  </ul>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {(office.tags ?? []).map((tag) =>
                    <span className="rounded-full bg-neutral-100 px-2.5 py-1 text-[11px] font-medium text-neutral-600" key={tag}>{tag}</span>
                    )}
                  </div>
                </div>
              </article>);

          })}
        </div>

        <section id="network-advantages" className="mt-16 scroll-mt-32 border-t border-neutral-200/90 pt-14">
          <div className="mb-8 text-center">
            <h2 className="font-sans text-2xl font-bold text-neutral-900 sm:text-3xl">{network.networkAdvantagesHeading}</h2>
            {network.networkAdvantagesSubheading && <p className="mt-2 text-sm text-neutral-600">{network.networkAdvantagesSubheading}</p>}
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {(network.networkAdvantages ?? []).map((item) =>
            <div className="rounded-xl border border-neutral-200/90 bg-white p-5 shadow-sm" key={item.title}>
                <h3 className="font-sans text-base font-bold text-neutral-900">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-600">{item.desc}</p>
              </div>
            )}
          </div>
        </section>

        <section id="network-partners" className="mt-16 scroll-mt-32 border-t border-neutral-200/90 pt-14">
          <div className="mb-8 text-center">
            <h2 className="font-sans text-2xl font-bold text-neutral-900 sm:text-3xl">{network.networkPartnersHeading}</h2>
            {network.networkPartnersSubheading && <p className="mt-2 text-sm text-neutral-600">{network.networkPartnersSubheading}</p>}
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {(network.networkPartners ?? []).map((partner) =>
            <div className="flex items-center gap-3 rounded-xl border border-neutral-200/80 bg-white px-3 py-3 shadow-sm sm:gap-4 sm:px-4" key={`${partner.name}-${partner.category}`}>
                <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-neutral-200/90 bg-neutral-50">
                  {partner.logoUrl?.trim() ?
                <img src={partner.logoUrl.trim()} alt={partner.name} className="h-full w-full object-contain p-1.5" loading="lazy" /> :

                <span className="text-[10px] font-bold leading-tight text-neutral-500">{partner.name.slice(0, 2)}</span>
                }
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-neutral-900">{partner.name}</p>
                  <p className="mt-0.5 text-xs text-neutral-500">{partner.category}</p>
                </div>
              </div>
            )}
          </div>
        </section>

        <section id="network-cta" className="mt-16 scroll-mt-32 rounded-2xl border border-neutral-200/90 bg-white px-6 py-10 text-center shadow-sm sm:px-10">
          <h2 className="font-sans text-xl font-bold text-neutral-900 sm:text-2xl">{network.networkPageCtaTitle}</h2>
          {network.networkPageCtaSubtitle && <p className="mx-auto mt-3 max-w-xl text-sm text-neutral-600">{network.networkPageCtaSubtitle}</p>}
          <button type="button" className="anli-btn-dark click-bounce mt-6 px-8 py-2.5 text-sm" onClick={openCustomerService}>{uiText("layout.text_e7ab8be58d_2")}</button>
        </section>
      </div>
    </section>);

}

function NetworkPageMapVisual({ network, visible, activeRegionSlug, regionHighlights, onSelectRegionSlug }) {
  return (
    <div className="mb-8 sm:mb-10">
      <div className={cx("flex flex-col gap-6 sm:gap-8 transition-all duration-700 lg:flex-row lg:items-start lg:justify-between lg:gap-10", visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4")}>
        <div className="max-w-xl shrink-0">
          <h2 className="font-sans text-2xl font-bold tracking-tight text-neutral-900 sm:text-3xl lg:text-[2rem]">{network.title}</h2>
          <p className="mt-2.5 text-[13px] leading-relaxed text-neutral-600 sm:mt-3 sm:text-[15px]">{network.subtitle}</p>
        </div>
        <div className="grid w-full grid-cols-2 gap-1.5 sm:gap-2 sm:grid-cols-4 lg:max-w-[26rem] lg:shrink-0">
          {(network.globalStats ?? []).map((stat) =>
          <div className="rounded-xl border border-neutral-200/80 bg-white px-1.5 sm:px-2 py-2.5 sm:py-4 text-center shadow-[0_1px_3px_rgba(15,23,42,0.06)]" key={stat.label}>
              <div className="font-sans text-[1.08rem] sm:text-xl font-bold tabular-nums text-neutral-900 sm:text-2xl">{stat.value}{stat.suffix || ""}</div>
              <div className="mt-1 text-[10px] sm:text-[11px] font-medium text-neutral-500 sm:text-xs">{stat.label}</div>
            </div>
          )}
        </div>
      </div>
      <div className={cx("network-page-map-wrap relative mt-8 overflow-hidden rounded-2xl border border-neutral-200/70 bg-[hsl(220_16%_94%)] shadow-[0_4px_24px_-12px_rgba(15,23,42,0.12)] transition-all delay-100 duration-700 sm:mt-10", visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5")}>
        <NetworkMapSvg activeRegionSlug={activeRegionSlug} legendItems={network.legendItems ?? []} onSelectRegionSlug={onSelectRegionSlug} />
      </div>
      <div className={cx("mt-6 flex flex-col items-stretch gap-4 transition-all delay-150 duration-700 sm:mt-8 sm:flex-row sm:items-stretch sm:justify-between sm:gap-6", visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3")}>
        <div className="grid min-w-0 flex-1 grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3">
          {regionHighlights.slice(0, 4).map((region) =>
          <button
            type="button"
            key={region.slug || region.name}
            className={cx(
              "rounded-xl border px-1 py-3 text-center shadow-[0_1px_3px_rgba(15,23,42,0.06)] transition-all sm:py-4",
              region.slug === activeRegionSlug ?
              "border-amber-400/70 bg-amber-50/40 ring-2 ring-amber-400/30" :
              "border-neutral-200/90 bg-white hover:border-amber-300/60 hover:bg-amber-50/20"
            )}
            onClick={() => onSelectRegionSlug(region.slug)}>
            
              <div className="font-sans text-xl font-bold tabular-nums text-neutral-900 sm:text-[1.65rem]">{region.count}</div>
              <div className="mt-1 text-xs font-medium text-neutral-600">{region.label || region.name}</div>
            </button>
          )}
        </div>
        <div className="flex shrink-0 items-center justify-center">
          <a href="#network-offices" className="anli-btn-dark px-5 py-2.5 text-sm">{network.cta || uiText("network.text_e79c8be5ae")}</a>
        </div>
      </div>
    </div>);

}

function NetworkMapSvg({ activeRegionSlug, legendItems, onSelectRegionSlug }) {
  const nodeColor = (kind) => {
    if (kind === "hub") return "hsl(222 47% 18%)";
    if (kind === "center") return "hsl(222 42% 24%)";
    if (kind === "branch") return "hsl(222 32% 36%)";
    return "hsl(220 14% 72%)";
  };

  return (
    <div className="network-map-shell">
      <div className="network-map-grid" aria-hidden="true" />
      <div className="network-map-fade" aria-hidden="true" />
      <svg viewBox="0 0 1000 400" className="network-map-svg" preserveAspectRatio="xMidYMid meet" role="img" aria-label={uiText("network.text_e585a8e790_3")}>
        <defs>
          <linearGradient id="network-map-fade" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="white" stopOpacity="0.2" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </linearGradient>
        </defs>
        <g opacity="0.14" fill="none" stroke="hsl(220 16% 45%)" strokeWidth="1">
          <path d="M 140 255 Q 180 200 260 215 Q 320 225 350 280 Q 300 330 200 320 Q 120 305 140 255" />
          <path d="M 380 120 Q 450 90 520 100 Q 580 110 620 150 Q 600 200 520 195 Q 440 190 380 120" />
          <path d="M 700 200 Q 780 170 920 200 Q 940 250 880 290 Q 760 300 700 200" />
          <path d="M 520 260 Q 580 245 640 265 Q 620 310 540 315 Q 500 300 520 260" />
        </g>
        {networkMapPaths.map((path, index) =>
        <path key={path.key} d={path.d} fill="none" stroke="hsl(220 12% 55%)" strokeWidth="1.15" strokeLinecap="round" className="network-map-line network-map-line--play" style={{ animationDelay: `${index * 0.12}s` }} />
        )}
        <g transform="translate(768, 188)">
          <circle r="34" fill="none" stroke="hsl(222 47% 22% / 0.35)" strokeWidth="1" className="network-map-hub-ring" />
          <circle r="46" fill="none" stroke="hsl(222 47% 22% / 0.22)" strokeWidth="1" className="network-map-hub-ring network-map-hub-ring--delay" />
        </g>
        {networkMapNodes.map((node) => {
          const active = node.slug === activeRegionSlug;
          return (
            <g key={`${node.label}-${node.cx}`}>
              <title>{node.label}</title>
              <circle
                cx={node.cx}
                cy={node.cy}
                r={node.r + (active ? 2 : 0)}
                fill={nodeColor(node.kind)}
                stroke={active || node.kind === "hub" ? "hsl(38 92% 50%)" : "white"}
                strokeWidth={active || node.kind === "hub" ? 2 : 1}
                className="network-map-node"
                onClick={() => onSelectRegionSlug(node.slug)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelectRegionSlug(node.slug);
                  }
                }}
                tabIndex="0"
                role="button"
                aria-label={`${node.label}${uiText("network.network_map_filter_aria_suffix")}`} />
              
            </g>);

        })}
        <text x="768" y="166" textAnchor="middle" className="network-map-label">{uiText("network.text_e696b0e58a_2")}</text>
      </svg>
      <div className="network-map-legend">
        {legendItems.map((item) =>
        <div key={item.label}>
            <span className={item.color} />
            <strong>{item.label}</strong>
          </div>
        )}
      </div>
    </div>);

}

function LegacyNetworkSection({ preview = false }) {
  const network = content.network;
  const offices = (network.offices ?? []).slice(0, preview ? 3 : undefined);
  return (
    <Section id={preview ? "network" : undefined} title={network.title || network.officesHeading} subtitle={network.subtitle || network.officesSubheading}>
      <div className="stats-grid">
        {(network.globalStats ?? []).map((stat) =>
        <Stat key={stat.label} value={`${stat.value}${stat.suffix || ""}`} label={stat.label} />
        )}
      </div>
      {!preview &&
      <>
          <div className="network-map">
            <div className="map-line one" />
            <div className="map-line two" />
            <span className="map-node hq">{uiText("network.text_e696b0e58a_2")}</span>
            <span className="map-node hongkong" />
            <span className="map-node tokyo" />
            <span className="map-node dubai" />
            <span className="map-node london" />
            <span className="map-node nyc" />
            <div className="map-legend">
              {network.legendItems.map((item) => <span key={item.label}>{item.label}</span>)}
            </div>
          </div>
          <div className="region-tabs">
            {network.regions.map((region) =>
          <Link key={region.slug} to={`/regions/${slugOf(region.name)}`}>
                <strong>{region.count}</strong>
                <span>{region.name}</span>
              </Link>
          )}
          </div>
        </>
      }
      <div className="section-head small">
        <h2>{network.officesHeading}</h2>
        <p>{network.officesSubheading}</p>
      </div>
      <div className="card-grid three">
        {offices.map((office) =>
        <Link className="image-card" to={`/regions/${slugOf(network.regions.find((r) => r.slug === office.regionSlug)?.name || office.regionSlug)}`} key={office.city}>
            <img src={office.imageUrl} alt="" />
            <div>
              <h3>{office.city}</h3>
              <p>{office.address}</p>
              <span>{office.languages}</span>
            </div>
          </Link>
        )}
      </div>
      {!preview &&
      <>
          <CardGrid title={network.networkAdvantagesHeading} subtitle={network.networkAdvantagesSubheading} items={network.networkAdvantages ?? []} icon={<Globe2 />} />
          <div className="nested-section">
            <div className="section-head small">
              <h2>{network.networkPartnersHeading}</h2>
              <p>{network.networkPartnersSubheading}</p>
            </div>
            <div className="partner-grid">
              {network.networkPartners.map((partner) =>
            <article key={partner.name}>
                  <img src={partner.logoUrl} alt="" />
                  <div>
                    <h3>{partner.name}</h3>
                    <p>{partner.category}</p>
                  </div>
                </article>
            )}
            </div>
          </div>
          <section className="network-final-cta">
            <h2>{network.networkPageCtaTitle}</h2>
            <p>{network.networkPageCtaSubtitle}</p>
            <button className="btn btn-gold" type="button" onClick={openCustomerService}>{uiText("network.text_e7ab8be58d_3")}</button>
          </section>
        </>
      }
    </Section>);

}

function RegionDetail() {
  const { slug } = useParams();
  const network = content.network;
  const region = network.regions.find((item) => matchesSlug(item.name, slug) || item.slug === slug);
  useSeo(region ? `${region.name} | ${network.regionPageTitle || uiText("network.text_e58cbae59f")}` : uiText("network.cic_8"), network.regionPageSubtitle || uiText("network.text_e69caae689_3"));
  if (!region) {
    return (
      <PageShell title={uiText("network.cic_8")} subtitle={uiText("network.text_e69caae689_3")}>
        <section className="section center" />
      </PageShell>);

  }
  const offices = (network.offices ?? []).filter((office) => office.regionSlug === region.slug);

  return (
    <PageShell title={`${region.name} ${network.regionPageTitle || uiText("network.text_e69c8de58a_2")}`} subtitle={network.regionPageSubtitle}>
      <Section title={network.regionCoverageTitle} subtitle={region.cities.join(" / ")}>
        <div className="stats-grid compact">
          <Stat value={`${region.count}+`} label={uiText("network.text_e8a686e79b")} />
          <Stat value={region.cities.length} label={uiText("network.text_e9878de782")} />
          <Stat value="24h" label={uiText("network.text_e5938de5ba")} />
        </div>
        <div className="card-grid three">
          {offices.map((office) =>
          <article className="image-card" key={office.city}>
              <img src={office.imageUrl} alt="" />
              <div>
                <h3>{office.city}</h3>
                <p>{office.address}</p>
                <span>{office.hours}</span>
              </div>
            </article>
          )}
        </div>
      </Section>
    </PageShell>);

}

function ContactPage({ assessment = false }) {
  const contact = content.contact;
  const phone = contact.phone?.trim() || "";
  const email = contact.email?.trim() || "";
  const address = contact.address?.trim() || "";
  const showContactActions = hasCustomerService() || email;
  useSeo(assessment ? contact.assessmentBrowserTitle : contact.browserTitle, assessment ? contact.assessmentBrowserDescription : contact.browserDescription);
  return (
    <PageShell className="contact-hero" eyebrow={uiText("contact.text_e5aea2e688_2")} title={assessment ? contact.assessmentSeoH1 : contact.seoH1} subtitle={contact.pageSubtitle}>
      <section className="section contact-layout" id="form">
        <div className="contact-intro">
          <span className="eyebrow dark">{uiText("contact.text_e59ca8e7ba")}</span>
          <h2>{contact.formSectionTitle}</h2>
          <p>{uiText("contact.cic_9")}</p>
          <div className="contact-bullets">
            <span><Clock3 size={18} />{uiText("contact.24_2")}</span>
            <span><ShieldCheck size={18} />{uiText("contact.text_e8b387e696")}</span>
            <span><FileText size={18} />{uiText("contact.text_e58d94e58a")}</span>
          </div>
          <button className="copy-link" type="button" onClick={() => navigator.clipboard?.writeText(window.location.href)}>{uiText("contact.text_e5a48de588_3")}

          </button>
        </div>
        <ContactForm />
      </section>
      <section className="section contact-why">
        <div className="section-head">
          <h2>{contact.whyUs.title}</h2>
        </div>
        <div className="why-grid">
          {contact.whyUs.items.map((item) =>
          <article key={item.title}>
              <span><CheckCircle2 size={20} /></span>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </article>
          )}
        </div>
      </section>
      <section className="section contact-methods">
        <div className="section-head">
          <span className="eyebrow dark">{uiText("contact.contact_methods_eyebrow")}</span>
          <h2>{uiText("contact.text_e881afe7b5")}</h2>
        </div>
        <div className="method-grid">
          {phone && <a href={`tel:${phone.replaceAll(" ", "")}`}>
            <Phone size={26} />
            <strong>{uiText("contact.whatsapp")}</strong>
            <span>{phone}</span>
          </a>}
          {email && <a href={`mailto:${email}`}>
            <Mail size={26} />
            <strong>{uiText("contact.contact_method_email")}</strong>
            <span>{email}</span>
          </a>}
          {address && <div>
            <MapPin size={26} />
            <strong>{uiText("contact.text_e8bea6e585")}</strong>
            <span>{address}</span>
          </div>}
        </div>
        {showContactActions && <div className="method-actions">
          {hasCustomerService() && <button className="btn btn-gold" type="button" onClick={openCustomerService}>{contact.buttonText}</button>}
          {email && <a className="btn btn-light" href={`mailto:${email}`}>{uiText("contact.text_e58f91e980")}</a>}
        </div>}
      </section>
      <FAQSection title={contact.contactFaqTitle} subtitle={contact.contactFaqSubtitle} items={contact.contactFaqItems} />
    </PageShell>);

}

function ContactForm() {
  const [submitted, setSubmitted] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");
  const onSubmit = async (event) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    setSubmitted(false);
    setError("");
    setSubmitting(true);
    const form = new FormData(formEl);
    const payload = {
      name: String(form.get("name") || "").trim(),
      email: String(form.get("email") || "").trim(),
      phone: String(form.get("phone") || "").trim(),
      caseType: String(form.get("caseType") || "").trim(),
      message: String(form.get("message") || "").trim()
    };

    try {
      await jsonFetch("/api/contact-leads", {
        method: "POST",
        body: JSON.stringify(payload)
      });
      setSubmitted(true);
      formEl.reset();
      trackTikTok("CompleteRegistration");
      const nextUrl = content.contact.postSubmitWhatsAppUrl?.trim();
      if (nextUrl) {
        window.setTimeout(() => window.open(nextUrl, "_blank", "noopener,noreferrer"), 450);
      }
    } catch (err) {
      setError(err.message || uiText("contact.text_e68f90e4ba"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="contact-form" onSubmit={onSubmit}>
      <h2>{content.contact.pageTitle}</h2>
      <div className="form-grid">
        <label>{uiText("contact.text_e5a793e590")}

          <input name="name" required autoComplete="name" placeholder={uiText("contact.text_e682a8e79a")} />
        </label>
        <label>{uiText("contact.text_e794b5e5ad")}

          <input name="email" type="email" required autoComplete="email" placeholder="name@example.com" />
        </label>
        <label>{uiText("contact.text_e88194e7b3_4")}

          <input name="phone" autoComplete="tel" placeholder={uiText("contact.text_e590abe58c")} />
        </label>
        <label>{uiText("contact.text_e6a188e4bb")}

          <select name="caseType" defaultValue="">
            <option value="" disabled>{uiText("contact.text_e8afb7e980")}</option>
            {content.scamTypes.items.map((item) => <option key={item.title}>{item.title}</option>)}
          </select>
        </label>
      </div>
      <label>{uiText("contact.text_e6a188e683")}

        <textarea name="message" required rows="6" placeholder={uiText("contact.text_e8afb7e7ae")} />
      </label>
      <button className="btn btn-gold full" type="submit" disabled={submitting}>{submitting ? uiText("contact.text_e68f90e4ba_2") : uiText("contact.text_e68f90e4ba_3")}</button>
      {submitted && <p className="success-note">{content.contact.postSubmitWhatsAppUrl?.trim() ? uiText("contact.text_e5b7b2e694") : uiText("contact.text_e5b7b2e694_2")}</p>}
      {error && <p className="error-note">{error}</p>}
    </form>);

}

function LegalPage({ kind }) {
  const legal = content.legal[kind];
  useSeo(legal.browserTitle, legal.browserDescription);
  return (
    <PageShell eyebrow={uiText("contact.text_e4bfa1e4bb_2")} title={legal.h1} subtitle={legal.subtitle}>
      <div className="subpage-content section-padding pb-16 sm:pb-20 legal-online-doc">
        <article className="prose prose-neutral max-w-3xl mx-auto dark:prose-invert">
          {legal.lastUpdatedNote?.trim() ? <p className="mb-6 text-sm text-neutral-500">{legal.lastUpdatedNote.trim()}</p> : null}
          {legal.sections?.length ?
          legal.sections.map((section) =>
          <section className="mb-8 last:mb-0" key={section.heading}>
                  <h2 className="font-sans text-xl font-bold tracking-tight text-neutral-900 sm:text-2xl">{section.heading}</h2>
                  <p className="mt-3 text-[15px] sm:text-base leading-[1.75] text-neutral-700">{section.body}</p>
                </section>
          ) :
          legal.paragraphs?.map((p) =>
          <p className="text-[15px] sm:text-base leading-[1.75] text-neutral-700" key={p}>
                  {p}
                </p>
          )}
        </article>
      </div>
    </PageShell>);

}

function NewsPage() {
  const { id } = useParams();
  useSeo(`${content.news.sectionTitle} | CIC`, content.news.sectionSubtitle);
  return (
    <PageShell title={content.news.sectionTitle} subtitle={content.news.sectionSubtitle}>
      <section className="section legal-doc">
        <h2>{id}</h2>
        <p>{content.news.notFoundText}</p>
        <Link className="text-link" to="/">{content.news.backToNewsText || uiText("scamTypes.text_e8bf94e59b")}</Link>
      </section>
    </PageShell>);

}

function AdminPage() {
  useSeo(uiText("admin.admin_browser_title"), uiText("admin.text_e5908ee58f"));
  const [token, setToken] = React.useState(() => localStorage.getItem(ADMIN_TOKEN_KEY) || "");
  const [loginForm, setLoginForm] = React.useState({ username: "", password: "" });
  const [settings, setSettings] = React.useState({
    phone: content.contact?.phone || "",
    email: content.contact?.email || "",
    address: content.contact?.address || "",
    customerServiceScriptUrl: content.contact?.customerServiceScriptUrl || "",
    customerServiceKefuCode: content.contact?.customerServiceKefuCode || "",
    postSubmitWhatsAppUrl: content.contact?.postSubmitWhatsAppUrl || "",
    tiktokPixelId: content.seo?.tiktokPixelId || ""
  });
  const [leads, setLeads] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [error, setError] = React.useState("");
  const [activeAdminMenu, setActiveAdminMenu] = React.useState("settings");
  const [leadsPage, setLeadsPage] = React.useState(1);

  const authHeaders = React.useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const leadsPageCount = Math.max(1, Math.ceil(leads.length / ADMIN_LEADS_PAGE_SIZE));
  const currentLeadsPage = Math.min(leadsPage, leadsPageCount);
  const visibleLeads = leads.slice((currentLeadsPage - 1) * ADMIN_LEADS_PAGE_SIZE, currentLeadsPage * ADMIN_LEADS_PAGE_SIZE);

  const loadAdminData = React.useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const [nextContent, leadData] = await Promise.all([
      jsonFetch("/api/content"),
      jsonFetch("/api/admin/contact-leads", { headers: authHeaders })]
      );
      const effectiveContent = applyRuntimeContent(nextContent) || content;
      setSettings({
        phone: effectiveContent.contact?.phone || "",
        email: effectiveContent.contact?.email || "",
        address: effectiveContent.contact?.address || "",
        customerServiceScriptUrl: effectiveContent.contact?.customerServiceScriptUrl || "",
        customerServiceKefuCode: effectiveContent.contact?.customerServiceKefuCode || "",
        postSubmitWhatsAppUrl: effectiveContent.contact?.postSubmitWhatsAppUrl || "",
        tiktokPixelId: effectiveContent.seo?.tiktokPixelId || ""
      });
      setLeads(Array.isArray(leadData.leads) ? leadData.leads : []);
      setLeadsPage(1);
    } catch (err) {
      setError(err.message || uiText("admin.text_e5908ee58f_2"));
    } finally {
      setLoading(false);
    }
  }, [authHeaders, token]);

  React.useEffect(() => {
    loadAdminData();
  }, [loadAdminData]);

  const handleLogin = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);
    try {
      const data = await jsonFetch("/api/auth/login", {
        method: "POST",
        body: JSON.stringify(loginForm)
      });
      localStorage.setItem(ADMIN_TOKEN_KEY, data.token);
      setToken(data.token);
      setMessage(uiText("admin.text_e799bbe5bd"));
    } catch (err) {
      setError(err.message || uiText("admin.text_e799bbe5bd_2"));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");
    setLoading(true);
    try {
      const nextPatch = {
        contact: {
          phone: settings.phone.trim(),
          email: settings.email.trim(),
          address: settings.address.trim(),
          customerServiceScriptUrl: settings.customerServiceScriptUrl.trim(),
          customerServiceKefuCode: settings.customerServiceKefuCode.trim(),
          postSubmitWhatsAppUrl: settings.postSubmitWhatsAppUrl.trim()
        },
        seo: {
          tiktokPixelId: settings.tiktokPixelId.trim()
        }
      };
      await jsonFetch("/api/content", {
        method: "PUT",
        headers: authHeaders,
        body: JSON.stringify(nextPatch)
      });
      applyRuntimeContent(nextPatch);
      setMessage(uiText("admin.text_e9858de7bd"));
    } catch (err) {
      setError(err.message || uiText("admin.text_e4bf9de5ad"));
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setToken("");
    setLeads([]);
    setMessage("");
    setError("");
  };

  if (!token) {
    return (
      <div className="admin-page admin-login-page">
        <main>
          <form className="admin-card admin-login-card" onSubmit={handleLogin}>
            <img src="/logo.png" alt="" />
            <h1>{uiText("admin.admin_product_name")}</h1>
            <label>{uiText("admin.text_e794a8e688")}

              <input value={loginForm.username} onChange={(event) => setLoginForm((form) => ({ ...form, username: event.target.value }))} autoComplete="username" />
            </label>
            <label>{uiText("admin.text_e5af86e7a0")}

              <input value={loginForm.password} onChange={(event) => setLoginForm((form) => ({ ...form, password: event.target.value }))} type="password" autoComplete="current-password" />
            </label>
            <button className="btn btn-gold full" disabled={loading} type="submit">{loading ? uiText("admin.text_e799bbe5bd_3") : uiText("admin.text_e799bbe5bd_4")}</button>
            {error && <p className="error-note">{error}</p>}
          </form>
        </main>
      </div>);

  }

  return (
    <div className="admin-page">
      <aside>
        <img src="/logo.png" alt="" />
        <strong>{uiText("admin.admin_product_name")}</strong>
        <span>{uiText("admin.text_e88194e7b3_5")}</span>
        <nav className="admin-menu" aria-label={uiText("admin.text_e5908ee58f_3")}>
          <button className={activeAdminMenu === "settings" ? "active" : ""} type="button" onClick={() => setActiveAdminMenu("settings")}>{uiText("admin.text_e88194e7b3_6")}</button>
          <button className={activeAdminMenu === "leads" ? "active" : ""} type="button" onClick={() => setActiveAdminMenu("leads")}>{uiText("admin.text_e592a8e8af_2")}</button>
        </nav>
        <button type="button" onClick={loadAdminData}>{uiText("admin.text_e588b7e696")}</button>
        <button type="button" onClick={handleLogout}>{uiText("admin.text_e98080e587")}</button>
      </aside>
      <main>
        <div className="admin-heading">
          <div>
            <h1>{uiText("admin.text_e5908ee58f")}</h1>
            <p>{uiText("admin.contact_whatsapp_tiktok_pixel")}</p>
          </div>
        </div>
        {message && <p className="success-note">{message}</p>}
        {error && <p className="error-note">{error}</p>}
        {activeAdminMenu === "settings" && <form className="admin-card admin-settings-form" onSubmit={handleSave}>
          <h2>{uiText("admin.text_e88194e7b3_7")}</h2>
          <div className="admin-form-grid">
            <label>{uiText("admin.text_e794b5e8af")}

              <input value={settings.phone} onChange={(event) => setSettings((value) => ({ ...value, phone: event.target.value }))} />
            </label>
            <label>{uiText("admin.text_e982aee7ae")}

              <input value={settings.email} onChange={(event) => setSettings((value) => ({ ...value, email: event.target.value }))} type="email" />
            </label>
            <label>{uiText("admin.text_e59cb0e59d")}

              <input value={settings.address} onChange={(event) => setSettings((value) => ({ ...value, address: event.target.value }))} />
            </label>
            <label>{uiText("admin.customer_service_script_url")}

              <input value={settings.customerServiceScriptUrl} onChange={(event) => setSettings((value) => ({ ...value, customerServiceScriptUrl: event.target.value }))} placeholder={CUSTOMER_SERVICE_SCRIPT_URL} />
            </label>
            <label>{uiText("admin.customer_service_kefu_code")}

              <input value={settings.customerServiceKefuCode} onChange={(event) => setSettings((value) => ({ ...value, customerServiceKefuCode: event.target.value }))} placeholder={CUSTOMER_SERVICE_KEFU_CODE} />
            </label>
            <label>{uiText("admin.text_e8a1a8e58d")}

              <input value={settings.postSubmitWhatsAppUrl} onChange={(event) => setSettings((value) => ({ ...value, postSubmitWhatsAppUrl: event.target.value }))} placeholder={uiText("admin.whatsapp_2")} />
            </label>
            <label>
              {uiText("admin.admin_tiktok_pixel_id")}
              <input value={settings.tiktokPixelId} onChange={(event) => setSettings((value) => ({ ...value, tiktokPixelId: event.target.value }))} />
            </label>
          </div>
          <p className="admin-help">{uiText("admin.post_tiktok_completeregistration")}</p>
          <div className="admin-inline-note">
            <h3>{uiText("admin.tiktok")}</h3>
            <div className="admin-event-list">
              <p><strong>{uiText("admin.admin_event_page_view")}</strong>{uiText("admin.text_e2809420e5")}</p>
              <p><strong>{uiText("admin.admin_event_click_button")}</strong>{uiText("admin.customer_service")}</p>
              <p><strong>{uiText("admin.admin_event_contact")}</strong>{uiText("admin.description")}</p>
              <p><strong>{uiText("admin.admin_event_complete_registration")}</strong>{uiText("admin.post_api_contact_leads")}</p>
            </div>
            <p className="admin-help">{uiText("admin.tiktok_url_pixel_helper_all")}</p>
          </div>
          <button className="btn btn-gold" disabled={loading} type="submit">{loading ? uiText("admin.text_e4bf9de5ad_2") : uiText("admin.text_e4bf9de5ad_3")}</button>
        </form>}

        {activeAdminMenu === "leads" && <section className="admin-card">
          <div className="admin-card-title">
            <h2>{uiText("admin.text_e592a8e8af_2")}</h2>
            <span>{leads.length ? `${(currentLeadsPage - 1) * ADMIN_LEADS_PAGE_SIZE + 1}-${Math.min(currentLeadsPage * ADMIN_LEADS_PAGE_SIZE, leads.length)} / ${leads.length} ${uiText("admin.admin_leads_count_unit")}` : uiText("admin.0")}</span>
          </div>
          <div className="admin-leads">
            {leads.length ? visibleLeads.map((lead) =>
            <article key={lead.id || `${lead.email}-${lead.at}`}>
                <div>
                  <strong>{lead.name || uiText("admin.text_e69caae5a1")}</strong>
                  <span>{lead.at ? new Date(lead.at).toLocaleString() : ""}</span>
                </div>
                <p>{lead.message}</p>
                <dl>
                  <dt>{uiText("admin.text_e982aee7ae")}</dt><dd>{lead.email || "-"}</dd>
                  <dt>{uiText("admin.text_e794b5e8af")}</dt><dd>{lead.phone || "-"}</dd>
                  <dt>{uiText("admin.text_e7b1bbe59e")}</dt><dd>{lead.caseType || "-"}</dd>
                  <dt>{uiText("admin.admin_lead_ip")}</dt><dd>{lead.ip || "-"}</dd>
                </dl>
              </article>
            ) : <p className="admin-help">{uiText("admin.text_e69a82e697")}</p>}
          </div>
          {leads.length > ADMIN_LEADS_PAGE_SIZE &&
          <div className="admin-pagination">
              <button type="button" disabled={currentLeadsPage <= 1} onClick={() => setLeadsPage((page) => Math.max(1, page - 1))}>{uiText("admin.text_e4b88ae4b8_2")}</button>
              <span>{uiText("admin.text_e7acac")}{currentLeadsPage} / {leadsPageCount}{uiText("admin.text_e9a1b5")}</span>
              <button type="button" disabled={currentLeadsPage >= leadsPageCount} onClick={() => setLeadsPage((page) => Math.min(leadsPageCount, page + 1))}>{uiText("admin.text_e4b88be4b8_2")}</button>
            </div>
          }
        </section>}
      </main>
    </div>);

}

function NotFoundPage() {
  const systemTexts = content.systemTexts ?? {
    notFoundTitle: uiText("system.cic_10"),
    notFoundMessage: uiText("system.text_e682a8e8ae"),
    notFoundBackHomeText: uiText("scamTypes.text_e8bf94e59b")
  };
  useSeo(systemTexts.notFoundTitle, systemTexts.notFoundMessage);
  return (
    <PageShell title={systemTexts.notFoundTitle} subtitle={systemTexts.notFoundMessage}>
      <section className="section center">
        <Link className="btn btn-gold" to="/">{systemTexts.notFoundBackHomeText}</Link>
      </section>
    </PageShell>);

}

function RiskSection() {
  const risk = content.risk;
  const [visible, setVisible] = React.useState(false);
  const sectionRef = React.useRef(null);
  const warningStyles = [
  { Icon: CircleAlert, bar: "border-l-[5px] border-l-red-600", iconWrap: "bg-red-100 text-red-600" },
  { Icon: UserX, bar: "border-l border-l-neutral-200", iconWrap: "bg-orange-100 text-orange-600" },
  { Icon: Clock3, bar: "border-l border-l-neutral-200", iconWrap: "bg-amber-100 text-amber-700" },
  { Icon: Eye, bar: "border-l border-l-neutral-200", iconWrap: "bg-yellow-100 text-yellow-700" }];

  const emergencyStyles = [
  { Icon: UserX, border: "border-rose-200", badge: "bg-rose-500", iconColor: "text-rose-600" },
  { Icon: FileCheck, border: "border-orange-200", badge: "bg-orange-500", iconColor: "text-orange-600" },
  { Icon: Headphones, border: "border-slate-300", badge: "bg-slate-600", iconColor: "text-slate-600" }];


  React.useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setVisible(true);
    }, { threshold: 0.1 });
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="transition-all duration-700 ease-out motion-reduce:transition-none opacity-100 translate-y-0 motion-reduce:opacity-100 motion-reduce:translate-y-0" style={{ transitionDelay: "200ms" }}>
      <section id="risk" ref={sectionRef} className="apple-section anli-light-section">
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-9 sm:mb-12 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <h2 className="anli-section-title">{risk.title}</h2>
            <p className="anli-section-subtitle mt-2.5 sm:mt-3">{risk.subtitle}</p>
          </div>

          <div className={`mb-7 sm:mb-10 rounded-xl border border-neutral-200/90 border-l-4 border-l-red-500 bg-white p-4 sm:p-6 shadow-sm transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{ transitionDelay: "120ms" }}>
            <h3 className="font-sans text-[14px] sm:text-base font-bold text-neutral-900">{risk.noticeTitle}</h3>
            <p className="mt-1.5 sm:mt-2 text-[12.5px] sm:text-sm leading-relaxed text-neutral-600">{risk.noticeText}</p>
          </div>

          <div className="mb-7 sm:mb-10 grid gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {(risk.warnings ?? []).map((item, index) => {
              const style = warningStyles[index] ?? warningStyles[0];
              const Icon = style.Icon;
              return (
                <div
                  className={`rounded-xl border border-neutral-200/90 bg-white py-3 sm:py-4 pl-3 sm:pl-4 pr-3 shadow-sm transition-all duration-700 ${style.bar} ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
                  style={{ transitionDelay: `${200 + 90 * index}ms` }}
                  key={item.title}>
                  
                  <div className={`mb-2.5 sm:mb-3 inline-flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-full ${style.iconWrap}`}>
                    <Icon className="h-4.5 w-4.5 sm:h-5 sm:w-5" aria-hidden="true" />
                  </div>
                  <h3 className="font-sans text-[14px] sm:text-base font-bold text-neutral-900">{item.title}</h3>
                  <p className="mt-1.5 sm:mt-2 text-[12.5px] sm:text-sm leading-relaxed text-neutral-600">{item.desc}</p>
                </div>);

            })}
          </div>

          <div className={`rounded-xl border border-neutral-200/90 bg-white p-4 sm:p-8 shadow-sm transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0"}`} style={{ transitionDelay: "400ms" }}>
            <h3 className="mb-4.5 sm:mb-6 text-center font-sans text-[15px] sm:text-lg font-bold text-neutral-900">{risk.emergencyTitle}</h3>
            <div className="grid gap-3 sm:gap-4 sm:grid-cols-3">
              {(risk.emergencySteps ?? []).map((item, index) => {
                const style = emergencyStyles[index] ?? emergencyStyles[0];
                const Icon = style.Icon;
                return (
                  <div className={`relative rounded-xl border-2 bg-white p-4.5 sm:p-5 shadow-sm ${style.border}`} key={item.num}>
                    <div className="relative mb-3.5 sm:mb-4 inline-flex">
                      <div className={`flex h-11 w-11 sm:h-12 sm:w-12 items-center justify-center rounded-xl bg-neutral-50 ${style.iconColor}`}>
                        <Icon className="h-5.5 w-5.5 sm:h-6 sm:w-6" aria-hidden="true" />
                      </div>
                      <span className={`absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold text-white ${style.badge}`}>{item.num}</span>
                    </div>
                    <h4 className="font-sans text-[15px] sm:text-base font-bold text-neutral-900">{item.title}</h4>
                    <p className="mt-1.5 sm:mt-2 text-[13px] sm:text-sm text-neutral-600">{item.desc}</p>
                  </div>);

              })}
            </div>
          </div>
        </div>
      </section>
    </div>);

}

function FAQSection({
  title = content.faq.title,
  subtitle = content.faq.subtitle,
  items = content.faq.items,
  sectionId = "faq",
  contactVariant = false
}) {
  const [visible, setVisible] = React.useState(false);
  const [openIndex, setOpenIndex] = React.useState(null);
  const sectionRef = React.useRef(null);
  const faqItems = items?.length ? items : content.faq.items;

  React.useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setVisible(true);
    }, { threshold: 0.1 });

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id={sectionId} ref={sectionRef} className={`apple-section scroll-mt-28 ${contactVariant ? "anli-light-section border-t border-neutral-200/60" : "anli-light-section"}`}>
      <div className={`mx-auto section-padding ${contactVariant ? "max-w-4xl" : "max-w-3xl"}`}>
        <div className={`mb-8 text-center transition-all duration-700 sm:mb-12 lg:mb-14 ${visible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
          <h2 className="anli-section-title">{title}</h2>
          <p className="anli-section-subtitle mt-2.5 sm:mt-3">{subtitle}</p>
        </div>
        <div className={`space-y-2.5 sm:space-y-3 ${contactVariant ? "sm:space-y-3.5" : ""}`}>
          {faqItems.map((item, index) => {
            const isOpen = openIndex === index;
            const buttonId = `faq-trigger-${sectionId}-${index}`;
            const panelId = `faq-panel-${sectionId}-${index}`;
            return (
              <div
                className={`faq-accordion-card border border-neutral-200/90 bg-white px-0 shadow-sm transition-all duration-500 overflow-hidden ${contactVariant ? "rounded-2xl" : "rounded-xl"} ${isOpen ? "faq-open" : ""} ${visible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}
                style={{ transitionDelay: `${120 + index * 70}ms` }}
                data-state={isOpen ? "open" : "closed"}
                key={item.q}>
                
                <h3 className="flex">
                  <button
                    id={buttonId}
                    type="button"
                    className="faq-accordion-trigger flex flex-1 items-center justify-between font-medium transition-all px-4 sm:px-6 py-3 sm:py-4 gap-2.5 sm:gap-3"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    data-state={isOpen ? "open" : "closed"}
                    onClick={() => setOpenIndex(isOpen ? null : index)}>
                    
                    <span className="flex items-start gap-3 flex-1 text-left min-w-0">
                      <span className="mt-0.5 flex h-7 w-7 sm:h-8 sm:w-8 shrink-0 items-center justify-center rounded-full border-2 border-gray-300 text-gray-500" aria-hidden="true">
                        <CircleHelp className="h-3.5 w-3.5 sm:h-4 sm:w-4 stroke-[2]" />
                      </span>
                      <span className="text-[13.5px] sm:text-base font-semibold text-foreground leading-snug pt-0.5">{item.q}</span>
                    </span>
                    <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} aria-hidden="true" />
                  </button>
                </h3>
                <div
                  id={panelId}
                  role="region"
                  aria-labelledby={buttonId}
                  className={`faq-accordion-content overflow-hidden text-sm transition-all duration-500 ${isOpen ? "faq-content-open" : "faq-content-closed"}`}
                  hidden={!isOpen}>
                  
                  <p className="text-foreground/75 text-[12.5px] sm:text-[15px] leading-relaxed px-4 sm:px-6 pb-4 sm:pb-5 pl-[calc(1rem+1.75rem+0.625rem)] sm:pl-[calc(1.5rem+2rem+0.75rem)] pr-4 sm:pr-6">
                    {item.a}
                  </p>
                </div>
              </div>);

          })}
        </div>
      </div>
    </section>);

}

function CTA({ title, subtitle, cta, href = "/contact", hideContactGrid = false }) {
  const [visible, setVisible] = React.useState(false);
  const sectionRef = React.useRef(null);
  const contactItems = [
  { Icon: Phone, label: content.contact.phone },
  { Icon: Mail, label: content.contact.email },
  { Icon: MapPin, label: content.contact.address }].
  filter((item) => item.label?.trim());

  React.useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setVisible(true);
    }, { threshold: 0.15 });

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="contact" ref={sectionRef} className="relative overflow-hidden">
      <div className="anli-dark-band">
        <div className={`max-w-7xl mx-auto section-padding py-8 sm:py-12 lg:py-14 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5 sm:gap-8 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="flex-1 min-w-0 text-center lg:text-left">
            <h2 className="text-[1.55rem] sm:text-3xl lg:text-[2rem] font-bold tracking-tight text-white leading-tight">{title}</h2>
            <p className="mt-2 text-[13px] sm:text-base text-gray-400 max-w-xl mx-auto lg:mx-0 leading-relaxed">{subtitle}</p>
          </div>
          <div className="flex justify-center lg:justify-end shrink-0">
            <button type="button" className="anli-btn-on-dark w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-3 text-[14px] sm:text-[15px] tracking-wide shadow-lg shadow-black/20" onClick={openCustomerService}>
              {cta}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
      {!hideContactGrid && contactItems.length > 0 &&
      <div className="border-t border-border/80 bg-background">
          <div className={`max-w-5xl mx-auto section-padding py-8 sm:py-12 transition-all duration-700 ${visible ? "opacity-100" : "opacity-0"}`} style={{ transitionDelay: "120ms" }}>
            <div className="grid sm:grid-cols-3 gap-6 sm:gap-6">
              {contactItems.map(({ Icon, label }) =>
            <div className="flex flex-col items-center gap-1.5 sm:gap-2 text-center text-muted-foreground" key={label}>
                  <div className="p-2.5 sm:p-3 rounded-full bg-white border border-gray-200 shadow-sm">
                    <Icon className="w-4.5 h-4.5 sm:w-5 sm:h-5 text-[hsl(var(--anli-charcoal))]" aria-hidden="true" />
                  </div>
                  <span className="text-[13px] sm:text-sm leading-relaxed">{label}</span>
                </div>
            )}
            </div>
          </div>
        </div>
      }
    </section>);

}

function CardGrid({ title, subtitle, items = [], icon }) {
  return (
    <div className="nested-section">
      {title && <div className="section-head small"><h2>{title}</h2>{subtitle && <p>{subtitle}</p>}</div>}
      <div className="card-grid three">
        {items.map((item) =>
        <article className="info-card" key={item.title}>
            {icon && <span className="card-icon">{icon}</span>}
            <h3>{item.title}</h3>
            {item.desc && <p>{item.desc}</p>}
          </article>
        )}
      </div>
    </div>);

}

function Stat({ value, label }) {
  return (
    <div className="stat">
      <strong>{value}</strong>
      <span>{label}</span>
    </div>);

}

export default App;
